/**
 * BrokerCommissionSettingsPage — Admin View
 *
 * Two modes:
 * A) "All Brokers" overview — stat cards, summary table, monthly pay data
 * B) Individual broker — config, reconciliation, loan tags tree, forecast calculator
 *
 * Route: /broker-commission-settings
 * Access: RequireRole(['super_admin', 'administrator'])
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  UserCog,
  Save,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Search,
  Tag,
  DollarSign,
  CreditCard,
  TrendingUp,
  User,
  Building2,
  CheckCircle2,
  Loader2,
  Filter,
  EyeOff,
  Eye,
  ArrowLeft,
  Plus,
  X,
  FileText,
  Briefcase,
  Users,
  HandCoins,
  AlertCircle,
} from 'lucide-react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { HelpButton } from '../../components/help/HelpButton';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';
import { resolveBrokerRecipientUsers } from '../../utils/recipientRosterResolution';
import { computeReconciliation } from '../../utils/drawAgainstCommission';
import {
  computeHurdleSchedule,
  hasRevenueHurdle,
  DEFAULT_HURDLE_CLAWBACK_BUCKET,
  DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH,
  DEFAULT_HURDLE_YEAR_END_RULE,
  type HurdleClawbackBucket,
  type HurdleYearEndRule,
} from '../../utils/revenueHurdle';
import { RevenueHurdleSchedule } from '../../components/brokers/RevenueHurdleSchedule';
import type { MonthlyReconciliation } from '../../utils/drawAgainstCommission';
import { findActiveConfig } from '../../utils/configResolution';
import { computeBrokerShare, type PaymentLeg } from '../../utils/commissionSplit';
import {
  buildFiscalYearGroups,
  currentFiscalYear,
  distinctLoanCount,
  fiscalYearLabel,
  fiscalYearToDateDetail,
  groupByFiscalYear,
  latestMonth,
  monthLabel,
  paymentRowKey,
  sumFiscalYear,
} from '../../utils/commissionTree';
import { useBrokerCommissionConfig } from '../../hooks/useBrokerCommissionConfig';
import { useBrokerLoanTags } from '../../hooks/useBrokerLoanTags';
import { useAllBrokersOverview } from '../../hooks/useAllBrokersOverview';
import type { OverviewPeriod } from '../../hooks/useAllBrokersOverview';
import { db, functions } from '../../lib/firebase';
import { useManualAdjustments } from '../../hooks/useManualAdjustments';
import { useBrokerVisibilityPreferences } from '../../hooks/useBrokerVisibilityPreferences';
import PerformanceTargetsTab from './PerformanceTargetsTab';
import MonthPicker from '../../components/common/MonthPicker';
import RecipientSplitControls, { type RecipientOptions } from './RecipientSplitControls';

/** Pull the persisted arrangement options off a raw split document.
 *  Absent fields stay absent so a legacy config round-trips byte-identical —
 *  the backend omits every field that equals its default, and re-sending an
 *  explicit default would defeat that. */
function pickRecipientOptions(raw: Record<string, unknown>): RecipientOptions {
  const out: RecipientOptions = {};
  if (raw.base === 'gross' || raw.base === 'broker_share') out.base = raw.base;
  if (typeof raw.reducesBrokerShare === 'boolean') out.reducesBrokerShare = raw.reducesBrokerShare;
  if (raw.appliesTo === 'every_loan' || raw.appliesTo === 'assigned_only') out.appliesTo = raw.appliesTo;
  if (raw.byIntroduction && typeof raw.byIntroduction === 'object' && !Array.isArray(raw.byIntroduction)) {
    out.byIntroduction = raw.byIntroduction as RecipientOptions['byIntroduction'];
  }
  return out;
}

// Page is gated by per-tenant `featureFlags.commission_settings === true` on
// the company doc. Activation is a runtime Firestore flip (no code deploy);
// replaces the prior hardcoded company-ID allowlist.

// ─── Types ───────────────────────────────────────────────────────────────────

interface BrokerOption {
  id: string;
  name: string;
  officeName: string;
}

interface CommissionVisibilityRecipient {
  id: string;
  displayName: string;
  email?: string;
  role: 'broker' | 'broker_assistant' | 'credit_analyst';
  hideGrossUpfrontCommission: boolean;
  hideGrossTrailCommission: boolean;
  /**
   * Assistant/analyst per-user leg allowlist (users/{uid}.visiblePaymentTypes).
   * null/absent = inherit the broker config baseline (both legs). Broker rows
   * ignore this: broker legs live on the commission config (visibleUpfront /
   * visibleTrail form state).
   */
  visiblePaymentTypes?: string[] | null;
}

/** Tri-state per payment leg per recipient (unified Payment Type Visibility card). */
type LegKey = 'upfront' | 'trail';
type LegVisibilityState = 'hidden' | 'payout_only' | 'full';

interface AllBrokersRow {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  officeId: string | null;
  officeName: string;
  baseSalaryCents: number;
  brokerIntroPct: number;
  companyIntroPct: number;
  /** False when the broker has no saved commission_config — no agreed rate exists. */
  isConfigured: boolean;
  /** Commission base before any split, always shown. */
  grossCommsCents: number;
  /** The broker's NET share — what they take home after recipient allocations.
   *  This is the reconciliation basis, matching the broker's own page. */
  commsCents: number;
  /** The broker's share BEFORE allocations, and what left it. Equal to
   *  commsCents / 0 for every broker with no assistant or analyst split. */
  grossShareCents: number;
  allocatedCents: number;
  totalPaidCents: number;
  amtOnTopCents: number;
  carryOverCents: number;
  visibleUpfront: boolean;
  visibleTrail: boolean;
  untaggedCount: number;
  templateIds: string[];
}

interface LoanTagRow {
  loanId: string;
  customerName: string;
  lender: string;
  paymentType: 'upfront' | 'trail' | 'arrears' | 'discharged' | 'clawback' | 'referrer_deduction';
  commissionCents: number;
  /** Settled loan amount on upfront, current balance on trail. `null` when the
   *  source cell was blank or the viewer has `hideLoanAmounts`; renders as an
   *  em dash. NEVER coerce to 0 - that reads as a real zero-value loan. */
  loanValueCents: number | null;
  introductionType: 'broker' | 'company' | 'self_sourced' | null;
  lockedPct: number | null;
  /** Phase 6 (3-tier) — captured trail % at tag time when introductionType
   *  === 'self_sourced'. Undefined / null for broker / company tags. */
  lockedTrailPct?: number | null;
  /** PR 1A-v4 (2026-05-14): per-loan analyst attribution. When set, this
   *  analyst is the sole recipient of analyst share for this loan; other
   *  analysts on the same broker get $0 for this loan. */
  creditAnalystId: string | null;
  /** Broker's GROSS share (post company-intro split, pre assistant deductions). */
  brokerShareCents: number;
  /** Sum of all assistant allocations out of brokerShareCents. */
  assistantTotalCents: number;
  /** Sum of all ANALYST allocations out of brokerShareCents. Was missing, so
   *  brokerNetCents overstated what the broker takes home. */
  analystTotalCents: number;
  /** What the broker nets: brokerShareCents − assistant − analyst. */
  brokerNetCents: number;
  month: string;
  year: number;
  isUntagged: boolean;
  isArrears: boolean;
  isDischarged: boolean;
  /** A third-party referrer fee the COMPANY funds. Shown so the broker can see
   *  the referrer was paid, but it carries no share. */
  isReferrerDeduction: boolean;
  isManualAdjustment?: boolean;
  manualAdjustmentId?: string;
  manualReason?: string;
}

// The loan tree's year > month > payment grouping lives in
// utils/commissionTree.ts (TreeYearGroup / TreeMonthGroup) and is shared with
// the broker's own page, so the two surfaces cannot drift apart.

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-500/20 text-blue-400',
  'bg-purple-500/20 text-purple-400',
  'bg-green-500/20 text-green-400',
  'bg-amber-500/20 text-amber-400',
  'bg-cyan-500/20 text-cyan-400',
  'bg-rose-500/20 text-rose-400',
];

/**
 * Can this row have an introduction split allocated against it?
 *
 * ONE predicate, used by every selection control, so the bulk checkboxes, the
 * per-row checkbox and the tag write can never disagree about what is taggable.
 * Four kinds of row carry no broker share and must never be selectable:
 *
 *   - arrears / discharged  status rows; the memo already zeroes their share
 *   - referrer_deduction    a company-funded third-party fee
 *   - manual adjustment     synthetic `manual_*` id; there is no loan to tag
 *
 * Before this predicate the two control families disagreed: the bulk
 * checkboxes let manual adjustments through, and the per-row checkbox let
 * arrears/discharged through, so an arrears row could be selected individually
 * and then bulk-tagged.
 */
function isTaggableRow(loan: LoanTagRow): boolean {
  return (
    !loan.isArrears &&
    !loan.isDischarged &&
    !loan.isReferrerDeduction &&
    !loan.isManualAdjustment
  );
}

function formatCurrencyWithCents(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Australian FY runs Jul–Jun. Returns the start year of the current FY. */
function getCurrentFYStartYear(): number {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
}

/** Dynamic FY label, e.g. "FY26" for FY starting Jul 2025. */
function getCurrentFYLabel(): string {
  return `FY${(getCurrentFYStartYear() + 1) % 100}`;
}

// Map period filter UI values to hook's OverviewPeriod type
const PERIOD_MAP: Record<string, OverviewPeriod> = {
  current_month: 'current_month',
  last_3: 'last_3_months',
  ytd: 'ytd',
  last_12: 'last_12_months',
};

// ─── Badge Components ────────────────────────────────────────────────────────

function PaymentTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    upfront: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    trail: 'bg-green-500/20 text-green-400 border-green-500/30',
    arrears: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    discharged: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    clawback: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    referrer_deduction: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  };
  const labels: Record<string, string> = { upfront: 'Upfront', trail: 'Trail', arrears: 'Arrears', discharged: 'Discharged', clawback: 'Clawback', referrer_deduction: 'Referrer Deduction' };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${styles[type] || 'bg-gray-600 text-gray-400 border-gray-500'}`}>
      {labels[type] || type}
    </span>
  );
}

function LoanStatusBadge({ loan }: { loan: LoanTagRow }) {
  if (loan.isManualAdjustment) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" title={loan.manualReason}>MANUAL</span>;
  // Ordered above isUntagged deliberately: a row that carries no share is never
  // "waiting to be tagged", and UNTAGGED would invite someone to tag it.
  if (loan.isReferrerDeduction) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-violet-500/20 text-violet-400" title="Third-party referrer fee. Paid by the company, not deducted from the broker's share.">REFERRER</span>;
  if (loan.isUntagged) return <span className="px-2 py-0.5 text-[0.625rem] font-bold rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">UNTAGGED</span>;
  if (loan.isDischarged) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30">DISCHARGED</span>;
  if (loan.isArrears) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">ARREARS</span>;
  return <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />;
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

const ACCENT_CLASSES: Record<string, string> = {
  blue: 'bg-gradient-to-r from-transparent via-blue-500 to-transparent',
  green: 'bg-gradient-to-r from-transparent via-green-500 to-transparent',
  purple: 'bg-gradient-to-r from-transparent via-purple-500 to-transparent',
  amber: 'bg-gradient-to-r from-transparent via-amber-500 to-transparent',
  red: 'bg-gradient-to-r from-transparent via-red-500 to-transparent',
};

function StatCard({ label, value, accent, detail, valueColor }: { label: string; value: string; accent: string; detail: string; valueColor?: string }) {
  return (
    <div className="rounded-lg shadow-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)' }}>
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${ACCENT_CLASSES[accent] || ACCENT_CLASSES.blue}`} />
      <div className="p-4 md:p-5 relative z-10">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-2xl md:text-3xl font-bold ${valueColor || 'text-white'}`}>{value}</p>
        <p className="text-xs text-gray-500 mt-1.5">{detail}</p>
      </div>
    </div>
  );
}

function StatCardWithIcon({ label, value, accent, detail, valueColor, icon, iconBg, iconColor }: {
  label: string; value: string; accent: string; detail: string; valueColor?: string;
  icon: React.ReactNode; iconBg: string; iconColor: string;
}) {
  return (
    <div className="rounded-lg shadow-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)' }}>
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${ACCENT_CLASSES[accent] || ACCENT_CLASSES.blue}`} />
      <div className="p-4 md:p-5 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl md:text-3xl font-bold ${valueColor || 'text-white'}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-1.5">{detail}</p>
          </div>
          <div className="p-2.5 rounded-xl" style={{ background: `linear-gradient(135deg, ${iconBg}, ${iconBg.replace('0.3', '0.1')})`, color: iconColor }}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Loading Spinner ─────────────────────────────────────────────────────────

function LoadingState({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      <p className="text-gray-400 text-sm">{message || 'Loading...'}</p>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const BrokerCommissionSettingsPage: React.FC = () => {
  const {
    userData,
    companyData,
    offices,
    teamMembers = [],
    userVisibleTemplates,
    loanBooks = [],
  } = useOutletContext<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewAsActive, isBrokerFilteredAdmin } = useAuth();
  // Source companyId from companyData.id so founder view-as resolves to the
  // impersonated tenant. useCompanyData prefers claimsCompanyId over
  // userData.companyId — using userData.companyId here would target the
  // founder's home tenant ('system') even while viewing another company,
  // so the callable was called for the wrong tenant and returned 0 brokers.
  const companyId = (companyData?.id || userData?.companyId) as string | undefined;
  // View-as is documented as read-only + audited on the banner. The legacy
  // isFounder bypass at enforceAuth.ts:350 means founder tokens can pass any
  // enforceRole gate (read OR write), so the read-only promise has to be
  // enforced HERE. This flag is the single source of truth that every write
  // handler below short-circuits on, and every write button/input disables
  // against. If a button slips through without `disabled`, the handler still
  // refuses.
  const isReadOnly = viewAsActive;
  const guardWrite = useCallback((action: string): boolean => {
    if (isReadOnly) {
      toast.error(`View-as is read-only — ${action} is not permitted. Exit view-as to make changes.`);
      return false;
    }
    // A broker-restricted admin is default-denied at the callable chokepoint,
    // so every write on this page (broker visibility, splits, tagging,
    // adjustments) would come back permission-denied. Refuse locally with the
    // one shared copy rather than let the server 403. Gating the guard covers
    // the whole surface; there is no second write path on this page.
    if (isBrokerFilteredAdmin) {
      toast.error(BROKER_FILTERED_ADMIN_COPY);
      return false;
    }
    return true;
  }, [isReadOnly, isBrokerFilteredAdmin]);

  // State
  const [selectedBroker, setSelectedBroker] = useState<string>(searchParams.get('broker') || 'all');
  // Tab state: "commission" (splits + salary + reconciliation) or "targets" (performance targets).
  // Persists via URL param so a deep link to ?broker=X&tab=targets lands on the right view.
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'commission' | 'targets'>(
    tabParam === 'targets' ? 'targets' : 'commission'
  );
  const periodFilterKey = companyId ? `valutten_period_filter_${companyId}` : null;
  const [periodFilter, setPeriodFilter] = useState(() => {
    if (!periodFilterKey) return 'ytd';
    try { return localStorage.getItem(periodFilterKey) || 'ytd'; } catch { return 'ytd'; }
  });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Office grouping state (all expanded by default)
  const [expandedOffices, setExpandedOffices] = useState<Set<string>>(new Set());
  const [officesInitialized, setOfficesInitialized] = useState(false);

  // Template filter state (persisted per company in localStorage)
  const templateFilterKey = companyId ? `valutten_template_filter_${companyId}` : null;
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(() => {
    if (!templateFilterKey) return new Set();
    try {
      const stored = localStorage.getItem(templateFilterKey);
      if (stored) return new Set(JSON.parse(stored));
    } catch { /* ignore */ }
    return new Set();
  });
  const [templateFilterOpen, setTemplateFilterOpen] = useState(false);
  const templateFilterRef = useRef<HTMLDivElement>(null);

  // Close template dropdown on click outside or Escape
  useEffect(() => {
    if (!templateFilterOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (templateFilterRef.current && !templateFilterRef.current.contains(e.target as Node)) {
        setTemplateFilterOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTemplateFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [templateFilterOpen]);

  // Hidden brokers — company-wide shared preference at
  // companies/{companyId}/commission_settings/hidden_brokers. Any admin
  // updates it; every admin in the tenant sees the same list in real time
  // via onSnapshot. Legacy localStorage values are seeded on first load
  // (one-shot) then cleared. See useBrokerVisibilityPreferences.
  const { hiddenBrokerIds, setHidden: setBrokerHidden } =
    useBrokerVisibilityPreferences(companyId);
  const [showHidden, setShowHidden] = useState(false);
  const toggleHideBroker = (brokerId: string) => {
    if (!guardWrite('hiding a broker')) return;
    setBrokerHidden(brokerId, !hiddenBrokerIds.has(brokerId));
  };

  // Initialize template filter with all templates selected (only if no localStorage value exists)
  const templateFilterInitRef = useRef(false);
  useEffect(() => {
    if (userVisibleTemplates?.length > 0 && !templateFilterInitRef.current) {
      templateFilterInitRef.current = true;
      // Only auto-select all if localStorage had no stored value
      if (selectedTemplateIds.size === 0 && !templateFilterKey) {
        setSelectedTemplateIds(new Set(userVisibleTemplates.map((t: any) => t.id)));
      } else if (selectedTemplateIds.size === 0) {
        // localStorage existed but was empty or missing — check if it was explicitly stored
        const stored = templateFilterKey ? localStorage.getItem(templateFilterKey) : null;
        if (!stored) {
          setSelectedTemplateIds(new Set(userVisibleTemplates.map((t: any) => t.id)));
        }
      }
    }
  }, [userVisibleTemplates]);

  // Config state (synced from hook when broker changes)
  const [brokerIntroPct, setBrokerIntroPct] = useState(60);
  const [companyIntroPct, setCompanyIntroPct] = useState(40);
  // PR C — 3-tier extensions. Only used when broker.commissionStructure === '3-tier'.
  const [trailBrokerIntroPct, setTrailBrokerIntroPct] = useState<number>(0);
  const [trailCompanyIntroPct, setTrailCompanyIntroPct] = useState<number>(0);
  const [upfrontSelfSourcedPct, setUpfrontSelfSourcedPct] = useState<number>(65);
  const [trailSelfSourcedPct, setTrailSelfSourcedPct] = useState<number>(65);
  const [trailVisibilityMode, setTrailVisibilityMode] = useState<'all' | 'self_sourced_only'>('all');
  const [commissionStructure, setCommissionStructure] = useState<'2-tier' | '3-tier'>('2-tier');
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [structureSaving, setStructureSaving] = useState(false);
  const [visibleUpfront, setVisibleUpfront] = useState(true);
  const [visibleTrail, setVisibleTrail] = useState(false);
  const [baseSalary, setBaseSalary] = useState('0');
  // Monthly revenue hurdle (2026-10-06). Dollars as typed, like baseSalary;
  // converted to integer cents on save. Empty string = no hurdle.
  const [revenueHurdle, setRevenueHurdle] = useState('');
  const [hurdleClawbackBucket, setHurdleClawbackBucket] = useState<HurdleClawbackBucket>(DEFAULT_HURDLE_CLAWBACK_BUCKET);
  const [hurdleYearEndRule, setHurdleYearEndRule] = useState<HurdleYearEndRule>(DEFAULT_HURDLE_YEAR_END_RULE);
  const [hurdleFiscalYearStartMonth, setHurdleFiscalYearStartMonth] = useState<number>(DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH);
  const [trailStartMonth, setTrailStartMonth] = useState<string>('');
  // Assistants card state: in-flight assistantSplits being edited. Keyed by
  // user ID → { sharePct, displayName }. Hydrated from currentConfig on load
  // and sent to updateBrokerCommissionConfig on save.
  const [assistantSplits, setAssistantSplits] = useState<
    Record<string, { sharePct: number; displayName: string } & RecipientOptions & {
      upfrontSharePct?: number;
      trailSharePct?: number;
    }>
  >({});
  // List of eligible assistant users — loaded from Firestore users where
  // role='broker_assistant' AND brokerDocId=selectedBroker AND companyId match.
  const [availableAssistants, setAvailableAssistants] = useState<
    Array<{
      id: string;
      displayName: string;
      email: string;
      hideGrossUpfrontCommission: boolean;
      hideGrossTrailCommission: boolean;
      visiblePaymentTypes: string[] | null;
    }>
  >([]);
  const [brokerRecipientUsers, setBrokerRecipientUsers] = useState<
    CommissionVisibilityRecipient[]
  >([]);
  // Pending tri-state selections (per recipient per leg), persisted by the
  // page Save button alongside the rest of the commission configuration.
  const [pendingRecipientLegs, setPendingRecipientLegs] = useState<
    Record<string, Partial<Record<LegKey, LegVisibilityState>>>
  >({});
  const [showAddAssistantPicker, setShowAddAssistantPicker] = useState(false);

  // Credit Analyst allocations — per-broker config (PR 1A-v3, 2026-05-13).
  // Hydrated from currentConfig.creditAnalystSplits, sent to
  // updateBrokerCommissionConfig on save. Keyed by analyst record ID (=
  // Auth UID for login users, operational doc ID otherwise).
  //
  // Schema delta: separate upfront/trail percentages so admins can express
  // "Hanah takes 10% of Andrew's upfront commission but 0% of his trail."
  // Performance targets are now owned by the performance_targets collection
  // bound via `analystId` — see PerformanceTargetsTab (manageBrokerTargets).
  // PR #712's inline paymentMode/target fields were a design error and have
  // been removed from this page; the schema's strip-on-write behaviour
  // cleans up any stage residue.
  const [creditAnalystSplits, setCreditAnalystSplits] = useState<
    Record<
      string,
      {
        upfrontSharePct: number;
        trailSharePct: number;
        displayName: string;
      } & RecipientOptions
    >
  >({});
  const [availableAnalysts, setAvailableAnalysts] = useState<
    Array<{
      id: string;
      displayName: string;
      hasLogin: boolean;
      hideGrossUpfrontCommission: boolean;
      hideGrossTrailCommission: boolean;
      visiblePaymentTypes: string[] | null;
    }>
  >([]);
  const [showAddAnalystPicker, setShowAddAnalystPicker] = useState(false);

  // Loan tags state
  const [tagFilter, setTagFilter] = useState('all');
  const [tagSearch, setTagSearch] = useState('');
  const [loanSort, setLoanSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'loanId', dir: 'asc' });
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());
  const [editingPctLoanId, setEditingPctLoanId] = useState<string | null>(null);
  const [editingPctValue, setEditingPctValue] = useState<string>('');
  const currentYear = new Date().getFullYear();
  const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  // One clock on this page: the tile, the reconciliation table and the loan
  // tree all group by Australian financial year (identified by its end year).
  const currentFY = currentFiscalYear();
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set([currentFY]));
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set([currentMonth]));

  // Reconciliation collapse state
  const [expandedReconYears, setExpandedReconYears] = useState<Set<number>>(new Set([currentFY]));

  // Manual adjustment modal state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [voidConfirm, setVoidConfirm] = useState<{ isOpen: boolean; adjustmentId: string }>({ isOpen: false, adjustmentId: '' });
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [adjForm, setAdjForm] = useState({
    adjustmentType: '' as 'upfront_commission' | 'trail_commission' | 'clawback' | 'other' | '',
    loanId: '',
    customerName: '',
    lender: '',
    commissionDollars: '',
    gstDollars: '',
    gstAuto: true,
    periodMonth: currentMonth,
    settlementDate: '',
    loanAmountDollars: '',
    reason: '',
    introductionType: 'broker' as 'broker' | 'company' | 'self_sourced',
    introductionPct: brokerIntroPct,
  });
  // expandedAdjustments removed — manual adjustments now integrated into monthly view

  // Forecast state
  const [whatIfBrokerPct, setWhatIfBrokerPct] = useState(65);
  const [whatIfCompanyPct, setWhatIfCompanyPct] = useState(45);

  // ─── Broker list from Firestore ────────────────────────────────────────────
  const [brokerOptions, setBrokerOptions] = useState<BrokerOption[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(true);

  const selectedBrokerInfo = useMemo(
    () => brokerOptions.find((broker) => broker.id === selectedBroker),
    [selectedBroker, brokerOptions],
  );

  // Build office name lookup
  const officeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (offices) {
      for (const o of offices) {
        map.set(o.id, o.officeName || o.name || o.id);
      }
    }
    return map;
  }, [offices]);

  // ─── All Brokers Overview (callable function) ──────────────────────────────
  const overviewPeriod = PERIOD_MAP[periodFilter] || 'ytd';
  const {
    brokers: overviewBrokers,
    assistantRollup: overviewAssistantRollup,
    analystRollup: overviewAnalystRollup,
    loading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useAllBrokersOverview(companyId, overviewPeriod);

  // Derive broker options from the overview data (only real brokers, not pseudo-entries)
  // Filters out transfer aliases "(Tfr)", "(TRL PUR)" and company-level "Commission Employee" entries
  useEffect(() => {
    if (overviewLoading) {
      setBrokersLoading(true);
      return;
    }
    const options: BrokerOption[] = overviewBrokers
      .filter(b => {
        const name = b.brokerName || '';
        if (name.includes('(Tfr)') || name.includes('(TRL PUR)')) return false;
        if (name.includes('Commission Employee')) return false;
        if (name.includes('Pty Ltd,')) return false;
        return true;
      })
      .map(b => ({
        id: b.brokerDocId,
        name: b.brokerName,
        officeName: b.officeName || 'Unknown',
      }));
    options.sort((a, b) => a.name.localeCompare(b.name));
    setBrokerOptions(options);
    setBrokersLoading(false);
  }, [overviewBrokers, overviewLoading]);

  // Map overview data to AllBrokersRow for the table, computing reconciliation per broker
  // Filter out transfer aliases and company-level pseudo-brokers
  const allBrokersData: AllBrokersRow[] = useMemo(() => {
    return overviewBrokers
    .filter(b => {
      // Case-INSENSITIVE: the aggregator emits both '(TRL PUR)' and '(Trl Pur)'
      // for the same concept, so the original case-sensitive match leaked the
      // lower-case variants onto a page about broker pay splits. At Double Bay
      // that surfaced Glenn Mackay (Trl Pur), Lachlan Ede (Trl Pur) and
      // Daniel Pym (Trl Pur) — purchased trail books with no split payable —
      // while correctly hiding Tommy Nguyen (TRL PUR) and William Banham (TRL PUR).
      //
      // Deliberately does NOT filter '(old)': Selmina Aganovic (old) sits on the
      // PRIMARY entity (FP3P100, $115k) and is the company's own trail from her
      // salaried period, not a purchased book. Whether she belongs on a pay-split
      // page is a tenant decision, not something a name filter should make.
      const name = (b.brokerName || '').toLowerCase();
      if (name.includes('(tfr)') || name.includes('(trl pur)')) return false;
      if (name.includes('commission employee')) return false;
      if (name.includes('pty ltd,')) return false;
      return true;
    })
    .map((b, i) => {
      const salary = b.baseSalaryCents || 0;

      // A broker with NO saved commission_config has no agreed rate. Previously
      // the `?? 60` / `?? 40` fallbacks below invented one and multiplied the
      // commission base by it, presenting a fabricated dollar figure as though
      // it were the broker's share. That is wrong on its own, and it got worse
      // once the base was corrected to gross (the fabricated number grew).
      // Show gross, and render "Not configured" instead of a modelled amount.
      const isConfigured =
        b.brokerIntroductionPct != null || b.companyIntroductionPct != null;

      // Broker share, computed server-side (2026-08-12). This used to be
      // estimated here as `totalCommsCents * defaultIntroductionPct`, i.e. ONE
      // percentage against ONE total. That silently disagreed with the
      // drill-down on the same page, which values every loan individually:
      // it ignored the trail percentage, the per-loan tier tag and its locked
      // pct, the fact that arrears/discharged pay nothing, and the trail-start
      // and visibility rules. getAllBrokersCommissionOverview now applies all
      // of them, so both surfaces read from one implementation.
      // Reconcile from the broker's NET share — what they actually take home
      // after assistant/analyst allocations. The broker's own page has always
      // reconciled from net (see the "NET of assistant payouts" note on the
      // drill-down reconciliation below); this surface used the GROSS share, so
      // the two disagreed on Total Paid, Amt on Top and Carry Over for any
      // broker carrying a recipient allocation. Falls back to the share when an
      // older callable returns no net, so a cached client still reconciles.
      const grossShareCents = isConfigured ? (b.totalBrokerShareCents || 0) : 0;
      const allocatedCents = isConfigured ? (b.totalRecipientAllocatedCents ?? 0) : 0;
      const comms = isConfigured
        ? (b.totalBrokerNetCents ?? b.totalBrokerShareCents ?? 0)
        : 0;

      // Compute reconciliation from monthly broker NET
      let totalPaid = comms;
      let amtOnTop = 0;
      let carryOver = 0;
      const monthlyShare = b.monthlyBrokerNet ?? b.monthlyBrokerShare ?? [];
      if (isConfigured && monthlyShare.length > 0 && salary > 0) {
        const sorted = [...monthlyShare].sort((a, c) => a.month.localeCompare(c.month));
        // Reconcile against the broker's REAL salary periods. This used to pass a
        // single synthetic config carrying the CURRENT salary applied to every
        // month, so any broker whose salary ever changed (a raise, or unpaid
        // leave) showed a different Amt on Top and Carry Over here than on their
        // own page, which has always used the real history.
        //
        // Falls back to the old flattened behaviour when the callable did not
        // return a history, so a cached client cannot render a blank column.
        const salaryPeriods = (b.salaryHistory ?? []).length > 0
          ? b.salaryHistory!.map(h => ({
              effectiveFrom: new Date(h.effectiveFrom),
              effectiveTo: h.effectiveTo ? new Date(h.effectiveTo) : null,
              baseSalaryCents: h.baseSalaryCents,
            }))
          : [{ effectiveFrom: new Date(0), baseSalaryCents: salary }];
        const recon = computeReconciliation(sorted, salaryPeriods);
        if (recon.length > 0) {
          totalPaid = recon.reduce((s, r) => s + r.totalPaidCents, 0);
          amtOnTop = recon.reduce((s, r) => s + r.amtOnTopOfBaseCents, 0);
          carryOver = recon[recon.length - 1].carryOverCents;
        }
      }

      return {
        id: b.brokerDocId,
        name: b.brokerName,
        initials: getInitials(b.brokerName),
        avatarColor: AVATAR_COLORS[i % AVATAR_COLORS.length],
        officeId: b.officeId || null,
        officeName: b.officeName || 'Unknown',
        baseSalaryCents: salary,
        brokerIntroPct: b.brokerIntroductionPct ?? 0,
        companyIntroPct: b.companyIntroductionPct ?? 0,
        isConfigured,
        grossCommsCents: b.totalCommsCents || 0,
        commsCents: comms,
        // The broker's share BEFORE allocations, and what left it. Both are
        // surfaced so the column can show the deduction rather than silently
        // reporting a smaller number than the drill-down's per-loan column.
        grossShareCents,
        allocatedCents,
        totalPaidCents: totalPaid,
        amtOnTopCents: amtOnTop,
        carryOverCents: carryOver,
        visibleUpfront: b.visiblePaymentTypes?.includes('upfront') ?? true,
        visibleTrail: b.visiblePaymentTypes?.includes('trail') ?? false,
        untaggedCount: b.untaggedLoanCount || 0,
        templateIds: b.templateIds || [],
      };
    });
  }, [overviewBrokers]);

  // ─── Template-Filtered + Hidden Brokers ────────────────────────────────────
  const totalTemplateCount = userVisibleTemplates?.length || 0;
  const filteredBrokersData = useMemo(() => {
    let result = allBrokersData;
    // Apply template filter (skip when all selected — no filtering needed)
    const allSelected = selectedTemplateIds.size >= totalTemplateCount;
    if (!allSelected) {
      if (selectedTemplateIds.size === 0) {
        result = []; // Nothing selected → show no brokers
      } else {
        result = result.filter(b => {
          // Brokers with no template data get hidden when actively filtering
          if (b.templateIds.length === 0) return false;
          return b.templateIds.some(tid => selectedTemplateIds.has(tid));
        });
      }
    }
    // Apply hidden filter
    if (!showHidden) {
      result = result.filter(b => !hiddenBrokerIds.has(b.id));
    }
    return result;
  }, [allBrokersData, selectedTemplateIds, totalTemplateCount, hiddenBrokerIds, showHidden]);

  // ─── Office Grouping for All Brokers Table ─────────────────────────────────
  const officeGroups: { officeId: string; officeName: string; brokers: AllBrokersRow[] }[] = useMemo(() => {
    const groupMap = new Map<string, { officeId: string; officeName: string; brokers: AllBrokersRow[] }>();
    for (const broker of filteredBrokersData) {
      const key = broker.officeId || '__unknown__';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          officeId: key,
          officeName: broker.officeName,
          brokers: [],
        });
      }
      groupMap.get(key)!.brokers.push(broker);
    }
    // Sort groups: alphabetical by office name
    const groups = Array.from(groupMap.values());
    groups.sort((a, b) => a.officeName.localeCompare(b.officeName));
    return groups;
  }, [filteredBrokersData]);

  // Initialize expanded offices to all once data arrives
  useEffect(() => {
    if (officeGroups.length > 0 && !officesInitialized) {
      setExpandedOffices(new Set(officeGroups.map(g => g.officeId)));
      setOfficesInitialized(true);
    }
  }, [officeGroups, officesInitialized]);

  const toggleOfficeGroup = (officeId: string) => {
    setExpandedOffices(prev => {
      const next = new Set(prev);
      if (next.has(officeId)) {
        next.delete(officeId);
      } else {
        next.add(officeId);
      }
      return next;
    });
  };

  const toggleTemplateFilter = (templateId: string) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      if (templateFilterKey) localStorage.setItem(templateFilterKey, JSON.stringify([...next]));
      return next;
    });
  };

  // ─── Individual Broker: Commission Config ──────────────────────────────────
  const brokerDocId = selectedBroker !== 'all' ? selectedBroker : undefined;

  const {
    currentConfig,
    configHistory: rawConfigHistory,
    brokerLoans: bqLoans,
    trailStartMonth: trailStartMonthFromHook,
    loading: configLoading,
    refetchBigQueryData,
  } = useBrokerCommissionConfig(companyId, brokerDocId);

  // Sync config state when broker or config changes
  useEffect(() => {
    if (currentConfig) {
      setBrokerIntroPct(currentConfig.brokerIntroductionPct);
      setCompanyIntroPct(currentConfig.companyIntroductionPct);
      // PR C — hydrate 3-tier fields. Defaults preserve legacy parity:
      // null/undefined trail* means "fall back to upfront pct" in
      // computeBrokerShare; we surface that state in the UI as the same
      // upfront pct so admins see what currently happens.
      setTrailBrokerIntroPct(
        currentConfig.trailBrokerIntroductionPct ?? currentConfig.brokerIntroductionPct,
      );
      setTrailCompanyIntroPct(
        currentConfig.trailCompanyIntroductionPct ?? currentConfig.companyIntroductionPct,
      );
      setUpfrontSelfSourcedPct(currentConfig.upfrontSelfSourcedPct ?? 0);
      setTrailSelfSourcedPct(currentConfig.trailSelfSourcedPct ?? 0);
      setVisibleUpfront(currentConfig.visiblePaymentTypes?.includes('upfront') ?? true);
      setVisibleTrail(currentConfig.visiblePaymentTypes?.includes('trail') ?? true);
      const salaryDollars = currentConfig.baseSalaryCents
        ? (currentConfig.baseSalaryCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0';
      setBaseSalary(salaryDollars);
      setRevenueHurdle(
        currentConfig.monthlyRevenueHurdleCents
          ? (currentConfig.monthlyRevenueHurdleCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '',
      );
      setHurdleClawbackBucket(currentConfig.hurdleClawbackBucket ?? DEFAULT_HURDLE_CLAWBACK_BUCKET);
      setHurdleYearEndRule(currentConfig.hurdleYearEndRule ?? DEFAULT_HURDLE_YEAR_END_RULE);
      setHurdleFiscalYearStartMonth(currentConfig.hurdleFiscalYearStartMonth ?? DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH);
      setAssistantSplits(currentConfig.assistantSplits ?? {});
      // PR 1A-v3 hydration: read upfront/trail pcts with legacy `sharePct`
      // shim so any stage residue from PR #712 (or pre-PR-1A-v3 Entourage
      // configs that ever had analyst entries) loads cleanly. paymentMode
      // and target* fields on the doc are intentionally discarded — they
      // are no longer part of the schema (performance is owned by
      // performance_targets via analystId binding).
      const rawAnalystSplits = (currentConfig as { creditAnalystSplits?: Record<string, Record<string, unknown>> }).creditAnalystSplits ?? {};
      setCreditAnalystSplits(
        Object.fromEntries(
          Object.entries(rawAnalystSplits).map(([id, raw]) => {
            const legacy = typeof raw.sharePct === 'number' ? raw.sharePct : 0;
            const upfront = typeof raw.upfrontSharePct === 'number' ? raw.upfrontSharePct : legacy;
            const trail = typeof raw.trailSharePct === 'number' ? raw.trailSharePct : legacy;
            return [
              id,
              {
                upfrontSharePct: upfront,
                trailSharePct: trail,
                displayName: typeof raw.displayName === 'string' ? raw.displayName : '',
                ...pickRecipientOptions(raw),
              },
            ];
          }),
        ),
      );
    } else if (selectedBroker !== 'all') {
      // Reset to defaults for unconfigured broker
      setBrokerIntroPct(60);
      setCompanyIntroPct(40);
      setVisibleUpfront(true);
      setVisibleTrail(true);
      setBaseSalary('0');
      setRevenueHurdle('');
      setHurdleClawbackBucket(DEFAULT_HURDLE_CLAWBACK_BUCKET);
      setHurdleYearEndRule(DEFAULT_HURDLE_YEAR_END_RULE);
      setHurdleFiscalYearStartMonth(DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH);
      setAssistantSplits({});
      setCreditAnalystSplits({});
    }
  }, [currentConfig, selectedBroker]);

  // Load credit-analyst records for this company. Populates the "Add
  // Credit Analyst" dropdown in the allocations card. Company-wide list
  // (analysts can be allocated to multiple brokers).
  useEffect(() => {
    if (!companyId || selectedBroker === 'all') {
      setAvailableAnalysts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<
          { companyId: string },
          { records: Array<{ analystId: string; displayName: string; hasLogin: boolean; status: string }> }
        >(functions, 'listCreditAnalystRecords');
        const result = await fn({ companyId });
        if (cancelled) return;
        const active = (result.data.records ?? []).filter((r) => r.status === 'active');
        const withVisibility = await Promise.all(
          active.map(async (r) => {
            const userSnap = r.hasLogin ? await getDoc(doc(db, 'users', r.analystId)) : null;
            const user = userSnap?.data();
            return {
              id: r.analystId,
              displayName: r.displayName,
              hasLogin: r.hasLogin,
              hideGrossUpfrontCommission: user?.hideGrossUpfrontCommission === true,
              hideGrossTrailCommission: user?.hideGrossTrailCommission === true,
              visiblePaymentTypes:
                Array.isArray(user?.visiblePaymentTypes) && user.visiblePaymentTypes.length > 0
                  ? (user.visiblePaymentTypes as string[])
                  : null,
            };
          }),
        );
        if (!cancelled) setAvailableAnalysts(withVisibility);
      } catch (err) {
        if (!cancelled) {
          console.error('[BrokerCommissionSettings] listCreditAnalystRecords failed:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, selectedBroker]);

  // Resolve login-bearing broker-family recipients from the company roster
  // already loaded by AppLayout. Exact brokerDocId is authoritative. The
  // unique commission-name fallback covers legacy/canonical broker-document
  // drift without trusting a browser-supplied UID or picking ambiguously.
  useEffect(() => {
    if (!companyId || selectedBroker === 'all') {
      setAvailableAssistants([]);
      setBrokerRecipientUsers([]);
      return;
    }

    const companyRoster = (teamMembers as Array<Record<string, any> & { id: string }>).filter(
      (member) => !member.companyId || member.companyId === companyId,
    );
    const resolvedBrokerUsers = resolveBrokerRecipientUsers({
      companyId,
      selectedBrokerDocId: selectedBroker,
      selectedBrokerName: selectedBrokerInfo?.name,
      teamMembers: companyRoster,
    });

    setBrokerRecipientUsers(
      resolvedBrokerUsers.map((member) => ({
        id: member.id,
        displayName: member.displayName || member.email || member.id,
        email: member.email || '',
        role: 'broker' as const,
        hideGrossUpfrontCommission: member.hideGrossUpfrontCommission === true,
        hideGrossTrailCommission: member.hideGrossTrailCommission === true,
      })),
    );

    const resolvedBrokerDocIds = new Set<string>([
      selectedBroker,
      ...resolvedBrokerUsers
        .map((member) => member.brokerDocId)
        .filter((value): value is string => typeof value === 'string' && value.length > 0),
    ]);
    setAvailableAssistants(
      companyRoster
        .filter(
          (member) =>
            member.role === 'broker_assistant' &&
            resolvedBrokerDocIds.has(member.brokerDocId),
        )
        .map((member) => ({
          id: member.id,
          displayName: member.displayName || member.email || member.id,
          email: member.email || '',
          hideGrossUpfrontCommission: member.hideGrossUpfrontCommission === true,
          hideGrossTrailCommission: member.hideGrossTrailCommission === true,
          visiblePaymentTypes:
            Array.isArray(member.visiblePaymentTypes) && member.visiblePaymentTypes.length > 0
              ? (member.visiblePaymentTypes as string[])
              : null,
        })),
    );
  }, [companyId, selectedBroker, selectedBrokerInfo?.name, teamMembers]);

  // Discard pending tri-state selections when the admin switches broker:
  // the roster and config baselines they were relative to no longer apply.
  useEffect(() => {
    setPendingRecipientLegs({});
  }, [selectedBroker]);

  // Sync trail start month from hook (separate from versioned config — stored on broker doc)
  useEffect(() => {
    if (selectedBroker !== 'all') {
      setTrailStartMonth(trailStartMonthFromHook || '');
    }
  }, [trailStartMonthFromHook, selectedBroker]);

  // PR C — subscribe directly to broker doc for commissionStructure +
  // trailVisibilityMode. These fields live on the broker doc itself
  // (NOT on commission_config), so the existing config hook doesn't
  // surface them. Lightweight onSnapshot — fields rarely change.
  useEffect(() => {
    if (!companyId || selectedBroker === 'all') {
      setCommissionStructure('2-tier');
      setTrailVisibilityMode('all');
      return;
    }
    const brokerRef = doc(db, `companies/${companyId}/brokers/${selectedBroker}`);
    const unsub = onSnapshot(
      brokerRef,
      (snap) => {
        const data = snap.data();
        const struct = data?.commissionStructure === '3-tier' ? '3-tier' : '2-tier';
        const mode =
          data?.trailVisibilityMode === 'self_sourced_only' ? 'self_sourced_only' : 'all';
        setCommissionStructure(struct);
        setTrailVisibilityMode(mode);
      },
      (err) => {
        console.error('[BrokerCommissionSettings] broker doc listener error:', err);
        setCommissionStructure('2-tier');
        setTrailVisibilityMode('all');
      },
    );
    return () => unsub();
  }, [companyId, selectedBroker]);

  // Admin-only flag for showing the Activate 3-Tier toggle. Brokers (role=broker)
  // viewing their own data via /broker-commission would never reach this page —
  // route is gated by RequireRole(['super_admin', 'administrator']) — but
  // defence-in-depth.
  const isAdminViewer =
    userData?.role === 'super_admin' || userData?.role === 'administrator';
  const threeTierActive = commissionStructure === '3-tier';

  const commissionVisibilityRecipients = useMemo<CommissionVisibilityRecipient[]>(() => {
    const recipients: CommissionVisibilityRecipient[] = [
      ...brokerRecipientUsers,
      ...availableAssistants.map((assistant) => ({
        id: assistant.id,
        displayName: assistant.displayName,
        email: assistant.email,
        role: 'broker_assistant' as const,
        hideGrossUpfrontCommission: assistant.hideGrossUpfrontCommission,
        hideGrossTrailCommission: assistant.hideGrossTrailCommission,
        visiblePaymentTypes: assistant.visiblePaymentTypes,
      })),
      ...availableAnalysts
        .filter((analyst) => analyst.hasLogin && !!creditAnalystSplits[analyst.id])
        .map((analyst) => ({
          id: analyst.id,
          displayName: analyst.displayName,
          role: 'credit_analyst' as const,
          hideGrossUpfrontCommission: analyst.hideGrossUpfrontCommission,
          hideGrossTrailCommission: analyst.hideGrossTrailCommission,
          visiblePaymentTypes: analyst.visiblePaymentTypes,
        })),
    ];
    return Array.from(new Map(recipients.map((recipient) => [recipient.id, recipient])).values());
  }, [brokerRecipientUsers, availableAssistants, availableAnalysts, creditAnalystSplits]);

  // Effective tri-state for a recipient's leg: pending selection wins, then
  // the stored baseline. Broker legs come from the config form state
  // (visibleUpfront/visibleTrail); assistant/analyst legs from the user's
  // own visiblePaymentTypes (absent = inherit both legs from the config).
  const legStateFor = useCallback(
    (recipient: CommissionVisibilityRecipient, leg: LegKey): LegVisibilityState => {
      const pending = pendingRecipientLegs[recipient.id]?.[leg];
      if (pending) return pending;
      if (recipient.role === 'broker') {
        const legVisible = leg === 'upfront' ? visibleUpfront : visibleTrail;
        if (!legVisible) return 'hidden';
      } else if (
        Array.isArray(recipient.visiblePaymentTypes) &&
        recipient.visiblePaymentTypes.length > 0 &&
        !recipient.visiblePaymentTypes.includes(leg)
      ) {
        return 'hidden';
      }
      const grossHidden =
        leg === 'upfront'
          ? recipient.hideGrossUpfrontCommission
          : recipient.hideGrossTrailCommission;
      return grossHidden ? 'payout_only' : 'full';
    },
    [pendingRecipientLegs, visibleUpfront, visibleTrail],
  );

  // Record a tri-state selection. Nothing is persisted here; the page Save
  // button applies pending selections together with the rest of the config.
  const selectLegState = useCallback(
    (recipient: CommissionVisibilityRecipient, leg: LegKey, next: LegVisibilityState) => {
      if (recipient.role === 'broker') {
        // Broker leg visibility is part of the commission config payload.
        if (leg === 'upfront') setVisibleUpfront(next !== 'hidden');
        else setVisibleTrail(next !== 'hidden');
      }
      setPendingRecipientLegs((previous) => ({
        ...previous,
        [recipient.id]: { ...previous[recipient.id], [leg]: next },
      }));
    },
    [],
  );

  const handleToggleStructure = useCallback(async () => {
    if (!companyId || selectedBroker === 'all') return;
    if (!guardWrite('changing commission structure')) return;
    const newStructure: '2-tier' | '3-tier' = threeTierActive ? '2-tier' : '3-tier';
    setStructureSaving(true);
    try {
      const setStructure = httpsCallable(functions, 'setBrokerCommissionStructure');
      await setStructure({
        companyId,
        brokerDocId: selectedBroker,
        structure: newStructure,
      });
      toast.success(
        newStructure === '3-tier'
          ? 'Activated 3-tier commission structure for this broker.'
          : 'Reverted broker to 2-tier commission structure.',
      );
      setShowStructureModal(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change structure';
      toast.error(msg);
    } finally {
      setStructureSaving(false);
    }
  }, [companyId, selectedBroker, threeTierActive]);

  // ─── Individual Broker: Manual Adjustments ─────────────────────────────────
  const {
    adjustments: manualAdjustments,
    loading: adjustmentsLoading,
    error: adjustmentsError,
  } = useManualAdjustments(companyId, brokerDocId);

  // ─── Individual Broker: Loan Tags ──────────────────────────────────────────
  const {
    tags: rawLoanTags,
    loading: tagsLoading,
    hasMore: tagsHasMore,
  } = useBrokerLoanTags(companyId, brokerDocId);

  // Build tag lookup map from Firestore tags. PR 1A-v4 (2026-05-14):
  // creditAnalystId added to the map so the Loan Tagger UI can display
  // the current analyst tag per loan and let admins change it.
  const tagMap = useMemo(
    () =>
      new Map(
        rawLoanTags.map((t) => [
          t.loanId,
          {
            introductionType: t.introductionType,
            lockedPct: t.lockedPct,
            // Carried so the per-loan share can honour a locked TRAIL rate.
            // Present on the tag since the 3-tier work but dropped here, which
            // is why trail loans silently used the upfront locked pct.
            lockedTrailPct: t.lockedTrailPct,
            creditAnalystId: t.creditAnalystId,
          },
        ]),
      ),
    [rawLoanTags],
  );

  // Point-in-time config lookup via shared findActiveConfig utility.
  // Returns the full resolved config so callers get pcts AND the period's
  // assistantSplits (used to compute per-loan net after assistant payouts).
  const getConfigForMonth = useMemo(() => {
    return (
      periodMonth: string,
    ): {
      brokerPct: number;
      companyPct: number;
      // Leg + tier percentages, so the per-loan share can use computeBrokerShare
      // rather than applying the upfront rate to every payment type. Undefined
      // trail* means "inherit the upfront pct", which is the legacy behaviour
      // computeBrokerShare already implements.
      trailBrokerPct?: number;
      trailCompanyPct?: number;
      upfrontSelfSourcedPct?: number;
      trailSelfSourcedPct?: number;
      assistantSplits: Record<string, { sharePct: number; displayName: string }>;
      /** Analyst splits reduce the broker's share too. They were absent here,
       *  so the drill-down's "net" subtracted assistants only and reported a
       *  broker as taking home money that was allocated to an analyst. */
      creditAnalystSplits: Record<
        string,
        {
          upfrontSharePct: number;
          trailSharePct: number;
          displayName: string;
          appliesTo?: 'every_loan' | 'assigned_only';
          reducesBrokerShare?: boolean;
        }
      >;
    } => {
      const cfg = findActiveConfig(periodMonth, rawConfigHistory);
      if (cfg) {
        return {
          brokerPct: cfg.brokerIntroductionPct,
          companyPct: cfg.companyIntroductionPct,
          trailBrokerPct: cfg.trailBrokerIntroductionPct,
          trailCompanyPct: cfg.trailCompanyIntroductionPct,
          upfrontSelfSourcedPct: cfg.upfrontSelfSourcedPct,
          trailSelfSourcedPct: cfg.trailSelfSourcedPct,
          assistantSplits: cfg.assistantSplits ?? {},
          creditAnalystSplits:
            (cfg as { creditAnalystSplits?: Record<string, never> }).creditAnalystSplits ?? {},
        };
      }
      // No historical config yet → use current in-flight values (including
      // in-progress assistantSplits state so live UI changes preview).
      return {
        brokerPct: brokerIntroPct,
        companyPct: companyIntroPct,
        trailBrokerPct: trailBrokerIntroPct || undefined,
        trailCompanyPct: trailCompanyIntroPct || undefined,
        upfrontSelfSourcedPct,
        trailSelfSourcedPct,
        assistantSplits,
        creditAnalystSplits,
      };
    };
  }, [
    rawConfigHistory, brokerIntroPct, companyIntroPct, assistantSplits, creditAnalystSplits,
    trailBrokerIntroPct, trailCompanyIntroPct, upfrontSelfSourcedPct, trailSelfSourcedPct,
  ]);

  // Merge BigQuery loans with Firestore tags — shows ALL loans, not just tagged ones
  // Business rule: pre-March 2026 loans without explicit tags default to Company Introduction.
  // March 2026+ loans without explicit tags show as genuinely untagged until admin/broker tags them.
  const loanTags: LoanTagRow[] = useMemo(() => {
    let rows: LoanTagRow[];
    if (bqLoans.length > 0) {
      // Use BigQuery loans as the primary source, overlay Firestore tags
      rows = bqLoans.map(loan => {
        const tag = tagMap.get(loan.loanId) || null;
        // Date-based default: historical loans (pre-2026-03) default to 'company',
        // new loans require explicit tagging
        const introType = tag?.introductionType || ((loan.periodMonth || '') < '2026-03' ? 'company' : null);
        // Use locked percentage if available, otherwise derive from period-correct config
        const cfg = getConfigForMonth(loan.periodMonth || currentMonth);
        // Rows that carry NO broker share. Kept as ONE predicate so the share,
        // the assistant allocation, the tag controls and the untagged badge can
        // never drift apart. A third-party referrer fee joins arrears and
        // discharged here: the company funds it, so it must not reduce - and
        // must not be valued at - the broker's split.
        const isReferrerDeduction = !!loan.isReferrerDeduction;
        const isArrearsOrDischarged =
          loan.isArrears || loan.isDischarged || isReferrerDeduction;
        const [yearStr] = (loan.periodMonth || currentMonth).split('-');

        // LEG-AWARE (2026-08-11). Previously this applied the UPFRONT percentage
        // to every payment type, so a broker with a different trail rate — or a
        // 3-tier broker whose trail is 0% — had their trail commission valued at
        // the upfront rate. Thepdara Sylaprany: 20% company-intro applied to his
        // $70,355 of trail overstated him by $14,071 (25%).
        //
        // computeBrokerShare (utils/commissionSplit.ts) already encodes the full
        // rule — self_sourced tier, trail* override, and "trail inherits upfront
        // when the override is absent", which keeps every existing 2-tier broker
        // byte-identical. It had zero callers; this is the first.
        const leg: PaymentLeg = isReferrerDeduction
          ? 'referrer_deduction'
          : loan.isDischarged
            ? 'discharged'
            : loan.isArrears
              ? 'arrears'
              : ((loan.paymentType || 'upfront') === 'upfront' ? 'upfront' : 'trail');
        const isTrailLeg = leg !== 'upfront';
        // Locked percentages win over config, per leg. lockedTrailPct has existed
        // on the tag since the 3-tier work but was never read here.
        const lockedForLeg = isTrailLeg ? tag?.lockedTrailPct : tag?.lockedPct;

        const brokerShareCents = isArrearsOrDischarged
          ? 0
          : lockedForLeg != null
            ? Math.round(loan.commissionCents * lockedForLeg / 100)
            : computeBrokerShare(loan.commissionCents, introType, leg, {
                brokerIntroductionPct: cfg.brokerPct,
                companyIntroductionPct: cfg.companyPct,
                trailBrokerIntroductionPct: cfg.trailBrokerPct,
                trailCompanyIntroductionPct: cfg.trailCompanyPct,
                upfrontSelfSourcedPct: cfg.upfrontSelfSourcedPct,
                trailSelfSourcedPct: cfg.trailSelfSourcedPct,
                defaultIntroductionType: 'company',
                effectiveFrom: new Date(0),
                effectiveTo: null,
                baseSalaryCents: null,
              });
        // Compute assistant allocations out of the broker's gross share for
        // this period. Uses point-in-time assistantSplits so historical months
        // reflect whatever splits were in effect then.
        const assistantTotalCents = isArrearsOrDischarged
          ? 0
          : Object.values(cfg.assistantSplits).reduce(
              (s, v) => s + Math.round((brokerShareCents * v.sharePct) / 100),
              0,
            );
        // Analyst allocations reduce the broker's share on the loans they apply
        // to. Omitting them reported the broker as taking home money already
        // allocated to an analyst — live on prod for one broker whose whole
        // tagged book sits with a 1% analyst.
        //
        // `assigned_only` is the analyst DEFAULT (unlike assistants), so an
        // analyst only counts on loans tagged to them unless explicitly set to
        // every_loan. A business-borne split takes nothing from the broker.
        const analystTotalCents = isArrearsOrDischarged
          ? 0
          : Object.entries(cfg.creditAnalystSplits).reduce((sum, [analystId, v]) => {
              if (v.reducesBrokerShare === false) return sum;
              const applies =
                (v.appliesTo ?? 'assigned_only') === 'every_loan' ||
                tag?.creditAnalystId === analystId;
              if (!applies) return sum;
              const pct = isTrailLeg ? v.trailSharePct : v.upfrontSharePct;
              return sum + Math.round((brokerShareCents * (pct ?? 0)) / 100);
            }, 0);
        return {
          loanId: loan.loanId,
          customerName: loan.customerName || 'Unknown',
          lender: loan.lender || 'Unknown',
          paymentType: (isReferrerDeduction ? 'referrer_deduction' : loan.isDischarged ? 'discharged' : loan.isArrears ? 'arrears' : loan.paymentType || 'upfront') as LoanTagRow['paymentType'],
          commissionCents: loan.commissionCents,
          loanValueCents: loan.loanValueCents ?? null,
          introductionType: isArrearsOrDischarged ? null : introType,
          lockedPct: isArrearsOrDischarged ? null : (tag?.lockedPct ?? null),
          // PR 1A-v4: surface analyst tag so the Loan Tagger UI can render
          // the current selection. arrears/discharged rows can also carry
          // a tag (the tag persists across all commission rows for the loan).
          creditAnalystId: tag?.creditAnalystId ?? null,
          brokerShareCents,
          assistantTotalCents,
          analystTotalCents,
          brokerNetCents: brokerShareCents - assistantTotalCents - analystTotalCents,
          month: loan.periodMonth || currentMonth,
          year: parseInt(yearStr, 10),
          isUntagged: isArrearsOrDischarged ? false : !introType,
          isArrears: loan.isArrears,
          isDischarged: loan.isDischarged,
          isReferrerDeduction,
        };
      });
    } else {
      // Fallback: use Firestore tags only (if BigQuery data hasn't loaded yet)
      rows = rawLoanTags.map(tag => {
        const isTagged = !!tag.introductionType;
        const updatedMonth = tag.updatedAt
          ? `${tag.updatedAt.getFullYear()}-${String(tag.updatedAt.getMonth() + 1).padStart(2, '0')}`
          : currentMonth;
        const [yearStr] = updatedMonth.split('-');
        return {
          loanId: tag.loanId,
          customerName: tag.customerName || 'Unknown',
          lender: tag.lender || 'Unknown',
          paymentType: 'upfront' as const,
          commissionCents: 0,
          loanValueCents: null,
          introductionType: isTagged ? tag.introductionType : null,
          lockedPct: tag.lockedPct ?? null,
          creditAnalystId: tag.creditAnalystId ?? null,
          brokerShareCents: 0,
          assistantTotalCents: 0,
          analystTotalCents: 0,
          brokerNetCents: 0,
          month: updatedMonth,
          year: parseInt(yearStr, 10),
          isUntagged: !isTagged,
          isReferrerDeduction: false,
          isArrears: false,
          isDischarged: false,
        };
      });
    }

    // Append manual adjustments as synthetic rows — they appear in the month
    // they belong to (periodMonth) and affect that month's totals
    const adjustmentTypeToPaymentType: Record<string, LoanTagRow['paymentType']> = {
      upfront_commission: 'upfront',
      trail_commission: 'trail',
      clawback: 'clawback',
      other: 'upfront',
    };
    for (const adj of manualAdjustments) {
      const [yearStr] = adj.periodMonth.split('-');
      const adjIntroType = adj.introductionType || 'company';
      const adjPct = adj.introductionPct ?? (adjIntroType === 'broker' ? brokerIntroPct : companyIntroPct);
      const adjBrokerShareCents = Math.round(adj.commissionCents * adjPct / 100);
      const adjCfg = getConfigForMonth(adj.periodMonth);
      const adjAssistantTotalCents = Object.values(adjCfg.assistantSplits).reduce(
        (s, v) => s + Math.round((adjBrokerShareCents * v.sharePct) / 100),
        0,
      );
      // Manual adjustments inherit any existing analyst tag on the same loan
      // (if the loan was already tagged, subsequent adjustments continue to
      // pay the same analyst). For synthetic IDs that don't have a tag this
      // resolves to null.
      const adjTag = adj.loanId ? tagMap.get(adj.loanId) ?? null : null;
      rows.push({
        loanId: adj.loanId || `manual_${adj.id}`,
        customerName: adj.customerName || 'Manual Adjustment',
        lender: adj.lender || '—',
        // A manual adjustment is authored against the ledger, not ingested from
        // an aggregator file, so it has no loan value and is never a referrer fee.
        loanValueCents: null,
        isReferrerDeduction: false,
        paymentType: adjustmentTypeToPaymentType[adj.adjustmentType] || 'upfront',
        commissionCents: adj.commissionCents,
        introductionType: adjIntroType,
        lockedPct: adjPct,
        creditAnalystId: adjTag?.creditAnalystId ?? null,
        brokerShareCents: adjBrokerShareCents,
        assistantTotalCents: adjAssistantTotalCents,
        // Manual adjustments carry no per-loan analyst tag.
        analystTotalCents: 0,
        brokerNetCents: adjBrokerShareCents - adjAssistantTotalCents,
        month: adj.periodMonth,
        year: parseInt(yearStr, 10),
        isUntagged: false,
        isArrears: false,
        isDischarged: false,
        isManualAdjustment: true,
        manualAdjustmentId: adj.id,
        manualReason: adj.reason,
      });
    }

    return rows;
  }, [bqLoans, rawLoanTags, tagMap, getConfigForMonth, brokerIntroPct, companyIntroPct, currentMonth, manualAdjustments]);

  // ─── Reconciliation (from loan tags broker NET share + config history) ─────
  // NET of assistant payouts is what the broker actually takes home. Gross
  // (brokerShareCents) would be misleading when some is promised to assistants.
  const reconciliation: MonthlyReconciliation[] = useMemo(() => {
    if (!brokerDocId || loanTags.length === 0) return [];

    // Sum broker NET share per month from loan tags. Also track total
    // assistant payouts per month for the dedicated reconciliation column.
    const monthMap = new Map<string, number>();
    for (const tag of loanTags) {
      const m = tag.month.slice(0, 7); // 'YYYY-MM'
      monthMap.set(m, (monthMap.get(m) || 0) + tag.brokerNetCents);
    }
    const monthlyComms = Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, cents]) => ({ month, cents }));

    if (monthlyComms.length === 0) return [];

    // Build salary history from config history (includes effectiveTo for findActiveConfig)
    const salaryHistory = rawConfigHistory
      .filter(c => c.baseSalaryCents != null)
      .map(c => ({
        effectiveFrom: c.effectiveFrom,
        effectiveTo: c.effectiveTo ?? null,
        baseSalaryCents: c.baseSalaryCents!,
      }));

    return computeReconciliation(monthlyComms, salaryHistory);
  }, [brokerDocId, loanTags, rawConfigHistory]);

  // Monthly revenue hurdle schedule. Same rows and same NET basis as the
  // reconciliation above, so "Split Payout" here equals "Broker Comms" there.
  // Empty unless some config in the history carries a hurdle.
  const hurdleSchedule = useMemo(() => {
    if (!brokerDocId || loanTags.length === 0 || !hasRevenueHurdle(rawConfigHistory)) return [];
    return computeHurdleSchedule(loanTags, rawConfigHistory, { shareOf: l => l.brokerNetCents });
  }, [brokerDocId, loanTags, rawConfigHistory]);

  // Per-month RECIPIENT payouts, keyed by 'YYYY-MM'. Used alongside the
  // reconciliation table's existing columns — shows admins how much of the
  // broker's gross share is going to assistants and credit analysts each month.
  //
  // Analysts were omitted, which left the column unable to explain the gap
  // between the broker's share and their net once analyst allocations existed.
  const monthlyAssistantPayouts = useMemo(() => {
    const map = new Map<string, number>();
    for (const tag of loanTags) {
      const allocated = tag.assistantTotalCents + tag.analystTotalCents;
      if (allocated === 0) continue;
      const m = tag.month.slice(0, 7);
      map.set(m, (map.get(m) || 0) + allocated);
    }
    return map;
  }, [loanTags]);

  // Group reconciliation by FINANCIAL year (descending) for collapsible display.
  // Was calendar year, which put an unlabelled "2026" one line under an
  // "FY27 to date" tile and made the two look like they should agree.
  const reconYearGroups = useMemo(() => groupByFiscalYear(reconciliation), [reconciliation]);

  // No saved commission_config: getConfigForMonth falls back to the form's
  // in-flight 60/40, so every share figure would be an invented rate. The
  // all-brokers overview says "Not configured" for these brokers; this page
  // must too, instead of printing money at a rate nobody agreed.
  const notConfigured = rawConfigHistory.length === 0 && !currentConfig;

  // Newest period month with data. "FY27 to date" read in September means
  // Jul plus Aug to a broker; if August has not been loaded the page must say so.
  const latestLoadedMonth = useMemo(() => latestMonth(loanTags), [loanTags]);

  // Any assistant or analyst allocation on this broker. When there is one the
  // tree subtotals are NET (what the broker takes home, the reconciliation
  // basis) and the header says so; when there is none net equals share.
  const hasAllocations = useMemo(() => loanTags.some(t => t.assistantTotalCents !== 0 || t.analystTotalCents !== 0), [loanTags]);

  // Config history for display
  const configHistory = useMemo(() => {
    return rawConfigHistory.map(c => ({
      effectiveFrom: formatDateShort(c.effectiveFrom),
      effectiveTo: c.effectiveTo ? formatDateShort(c.effectiveTo) : null as string | null,
      brokerIntroPct: c.brokerIntroductionPct,
      companyIntroPct: c.companyIntroductionPct,
      baseSalaryCents: c.baseSalaryCents || 0,
      visibleUpfront: c.visiblePaymentTypes?.includes('upfront') ?? true,
      visibleTrail: c.visiblePaymentTypes?.includes('trail') ?? true,
      changedBy: c.createdBy || 'System',
    }));
  }, [rawConfigHistory]);

  const subtitle = useMemo(() => {
    if (selectedBroker === 'all') return 'Overview of all broker commission & pay data';
    return selectedBrokerInfo ? `${selectedBrokerInfo.name} — ${selectedBrokerInfo.officeName} Office` : 'Commission Settings';
  }, [selectedBroker, selectedBrokerInfo]);

  // Year > Month > Loans tree
  const yearGroups = useMemo(() => {
    let filtered = [...loanTags];
    if (tagFilter === 'untagged') filtered = filtered.filter(l => l.isUntagged);
    else if (tagFilter === 'broker') filtered = filtered.filter(l => l.introductionType === 'broker');
    else if (tagFilter === 'company') filtered = filtered.filter(l => l.introductionType === 'company');
    else if (tagFilter === 'arrears') filtered = filtered.filter(l => l.isArrears);
    else if (tagFilter === 'discharged') filtered = filtered.filter(l => l.isDischarged);
    // PR 1A-v4.1 (2026-05-14): per-analyst filter. "analyst:<id>" selects
    // only loans tagged to that analyst on this broker; "analyst-unassigned"
    // selects loans with no analyst tag (regardless of introduction type).
    else if (tagFilter === 'analyst-unassigned') filtered = filtered.filter(l => !l.creditAnalystId);
    else if (tagFilter.startsWith('analyst:')) {
      const aid = tagFilter.slice('analyst:'.length);
      filtered = filtered.filter(l => l.creditAnalystId === aid);
    }
    if (tagSearch) {
      const q = tagSearch.toLowerCase();
      filtered = filtered.filter(l => l.loanId.toLowerCase().includes(q) || l.customerName.toLowerCase().includes(q));
    }
    // EVERY payment row renders. From 2026-03-27 this collapsed rows to the
    // latest month per loanId::paymentType on the premise that Loan Market
    // "carries forward" upfront records; the server already returns one row
    // per (loan, period_month, payment_type), so the collapse hid every
    // earlier trail month, every subsequent-upfront top-up and every clawback
    // that was not the latest row, while the tiles and reconciliation above
    // kept them all. Measured 2026-09-07 on prod: $11.5M upfront and $30.9M
    // trail gross hidden fleet-wide. See utils/commissionTree.ts.
    //
    // Subtotals sum brokerNetCents, the same basis as the reconciliation
    // table, so a year here equals the year above it to the cent.
    const dir = loanSort.dir === 'asc' ? 1 : -1;
    const sortLoans = (a: LoanTagRow, b: LoanTagRow): number => {
      switch (loanSort.col) {
        case 'customer': return dir * a.customerName.localeCompare(b.customerName);
        case 'lender': return dir * (a.lender || '').localeCompare(b.lender || '');
        case 'type': return dir * a.paymentType.localeCompare(b.paymentType);
        case 'commission': return dir * (a.commissionCents - b.commissionCents);
        case 'loanValue': return dir * ((a.loanValueCents ?? -1) - (b.loanValueCents ?? -1));
        case 'share': return dir * (a.brokerNetCents - b.brokerNetCents);
        default: return dir * a.loanId.localeCompare(b.loanId);
      }
    };
    return buildFiscalYearGroups(filtered, { shareOf: l => l.brokerNetCents, sortLoans });
  }, [loanTags, tagFilter, tagSearch, loanSort]);

  const treeIsFiltered = tagFilter !== 'all' || tagSearch.trim() !== '';

  // Reconciliation lookup by month key (e.g., '2026-02') for pay summary in loan tags
  const reconByMonth = useMemo(() => {
    const map = new Map<string, { commsCents: number; baseSalaryCents: number; amtOnTopCents: number; carryOverCents: number }>();
    for (const r of reconciliation) {
      map.set(r.month, { commsCents: r.brokerCommsCents, baseSalaryCents: r.baseSalaryCents, amtOnTopCents: r.amtOnTopOfBaseCents, carryOverCents: r.carryOverCents });
    }
    return map;
  }, [reconciliation]);

  const allBrokersTotals = useMemo(() => ({
    commsCents: filteredBrokersData.reduce((s, b) => s + b.commsCents, 0),
    totalPaidCents: filteredBrokersData.reduce((s, b) => s + b.totalPaidCents, 0),
    baseSalaryCents: filteredBrokersData.reduce((s, b) => s + b.baseSalaryCents, 0),
    amtOnTopCents: filteredBrokersData.reduce((s, b) => s + b.amtOnTopCents, 0),
    carryOverCents: filteredBrokersData.reduce((s, b) => s + b.carryOverCents, 0),
    untaggedCount: filteredBrokersData.reduce((s, b) => s + b.untaggedCount, 0),
  }), [filteredBrokersData]);

  // Untagged count: use loanTags-based count which reflects the date-based default
  // (pre-March 2026 loans default to Company Intro, March 2026+ require explicit tagging)
  const untaggedCount = useMemo(() => loanTags.filter(l => l.isUntagged).length, [loanTags]);

  // FY-to-date totals: use reconciliation (broker net share from loan tags =
  // source of truth). Carries how many months are loaded so the tile can say
  // "1 month loaded" instead of implying a full year to date.
  const reconYtdTotals = useMemo(() => sumFiscalYear(reconciliation, currentFY), [reconciliation, currentFY]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const toggleYear = (y: number) => setExpandedYears(p => { const n = new Set(p); n.has(y) ? n.delete(y) : n.add(y); return n; });
  const toggleMonth = (m: string) => setExpandedMonths(p => { const n = new Set(p); n.has(m) ? n.delete(m) : n.add(m); return n; });
  const expandAll = () => { setExpandedYears(new Set(yearGroups.map(y => y.year))); setExpandedMonths(new Set(yearGroups.flatMap(y => y.months.map(m => m.month)))); };
  const collapseAll = () => { setExpandedYears(new Set([yearGroups[0]?.year].filter(Boolean) as number[])); setExpandedMonths(new Set()); };
  const toggleLoanSelection = (id: string) => setSelectedLoanIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const handleBrokerSwitch = (v: string) => {
    setSelectedBroker(v);
    setSelectedLoanIds(new Set());
    setTagFilter('all');
    setTagSearch('');
    setLoanSort({ col: 'loanId', dir: 'asc' });
    setExpandedYears(new Set([currentFY]));
    setExpandedMonths(new Set([currentMonth]));
    setEditingPctLoanId(null);
    setEditingPctValue('');
    setExpandedReconYears(new Set([currentFY]));
    v !== 'all' ? setSearchParams({ broker: v }) : setSearchParams({});
  };

  const handleSave = useCallback(async () => {
    if (!companyId || !brokerDocId) return;
    if (!guardWrite('saving commission settings')) return;
    setSaving(true);
    try {
      const visiblePaymentTypes: string[] = [];
      if (visibleUpfront) visiblePaymentTypes.push('upfront');
      if (visibleTrail) visiblePaymentTypes.push('trail');
      if (visiblePaymentTypes.length === 0) {
        toast.error('At least one payment type must be visible');
        setSaving(false);
        return;
      }
      // Same rule per assistant/analyst: an empty per-user allowlist is
      // unrepresentable (absent means unrestricted in the claim path).
      const fullyHiddenRecipient = commissionVisibilityRecipients.find(
        (recipient) =>
          recipient.role !== 'broker' &&
          legStateFor(recipient, 'upfront') === 'hidden' &&
          legStateFor(recipient, 'trail') === 'hidden',
      );
      if (fullyHiddenRecipient) {
        toast.error(
          `At least one payment type must be visible for ${fullyHiddenRecipient.displayName}. Use the Hide Commissions switch in Team Management to hide all financial data.`,
        );
        setSaving(false);
        return;
      }
      const parsedSalary = Math.round(parseFloat(baseSalary.replace(/,/g, '')) * 100);
      const hurdleText = revenueHurdle.replace(/,/g, '').trim();
      const parsedHurdle = hurdleText === '' ? null : Math.round(parseFloat(hurdleText) * 100);
      if (parsedHurdle !== null && (!Number.isFinite(parsedHurdle) || parsedHurdle <= 0)) {
        toast.error('Monthly revenue hurdle must be a positive amount, or left blank for no hurdle.');
        setSaving(false);
        return;
      }
      if (parsedHurdle !== null && !isNaN(parsedSalary) && parsedSalary > 0) {
        toast.error('A monthly revenue hurdle cannot be combined with a base salary. Clear one of them.');
        setSaving(false);
        return;
      }
      const normalizedTrailStartMonth = trailStartMonth || null;
      // Detect whether trailStartMonth actually changed — used to tailor the
      // success toast and drive the refetch below (P2-15).
      const trailStartMonthChanged = normalizedTrailStartMonth !== (trailStartMonthFromHook || null);

      // PR 1A-v3 two-budget cap (mirrors updateBrokerCommissionConfig server
      // validation). Assistant.sharePct applies uniformly to both upfront
      // and trail commission types, so it counts in BOTH budgets. The
      // analyst pool has separate upfront/trail percentages.
      const assistantSum = Object.values(assistantSplits).reduce(
        (s, v) => s + (v.sharePct || 0),
        0,
      );
      const analystUpfrontSum = Object.values(creditAnalystSplits).reduce(
        (s, v) => s + (v.upfrontSharePct || 0),
        0,
      );
      const analystTrailSum = Object.values(creditAnalystSplits).reduce(
        (s, v) => s + (v.trailSharePct || 0),
        0,
      );
      const upfrontBudgetSum = assistantSum + analystUpfrontSum;
      const trailBudgetSum = assistantSum + analystTrailSum;
      if (upfrontBudgetSum > 100) {
        toast.error(
          `Upfront budget is ${upfrontBudgetSum}% (${assistantSum}% assistants + ${analystUpfrontSum}% analyst upfront) — must be 100% or less.`,
        );
        setSaving(false);
        return;
      }
      if (trailBudgetSum > 100) {
        toast.error(
          `Trail budget is ${trailBudgetSum}% (${assistantSum}% assistants + ${analystTrailSum}% analyst trail) — must be 100% or less.`,
        );
        setSaving(false);
        return;
      }

      const updateFn = httpsCallable(functions, 'updateBrokerCommissionConfig');
      await updateFn({
        companyId,
        brokerDocId,
        brokerIntroductionPct: brokerIntroPct,
        companyIntroductionPct: companyIntroPct,
        // PR C — 3-tier extensions. Only sent when broker is activated as
        // 3-tier; otherwise the backend rejects them with failed-precondition.
        ...(threeTierActive && {
          trailBrokerIntroductionPct: trailBrokerIntroPct,
          trailCompanyIntroductionPct: trailCompanyIntroPct,
          upfrontSelfSourcedPct,
          trailSelfSourcedPct,
          trailVisibilityMode,
        }),
        visiblePaymentTypes,
        baseSalaryCents: isNaN(parsedSalary) ? null : parsedSalary,
        // null clears a hurdle; the options only travel with one.
        monthlyRevenueHurdleCents: parsedHurdle,
        ...(parsedHurdle !== null && {
          hurdleClawbackBucket,
          hurdleYearEndRule,
          hurdleFiscalYearStartMonth,
        }),
        trailStartMonth: normalizedTrailStartMonth,
        assistantSplits,
        creditAnalystSplits,
      });

      // Persist pending tri-state selections. Broker legs already went with
      // the config payload above; gross flags (all rows) and assistant/
      // analyst per-user legs go through the guarded visibility mutation.
      const toggleFn = httpsCallable(functions, 'toggleCommissionVisibility');
      const visibilityFailures: string[] = [];
      for (const recipient of commissionVisibilityRecipients) {
        const upfrontState = legStateFor(recipient, 'upfront');
        const trailState = legStateFor(recipient, 'trail');
        const payload: Record<string, unknown> = { companyId, userId: recipient.id };
        const localPatch: Partial<CommissionVisibilityRecipient> = {};
        let hasChange = false;

        if (recipient.role !== 'broker') {
          const desiredLegs = (['upfront', 'trail'] as LegKey[])
            .filter((leg) => (leg === 'upfront' ? upfrontState : trailState) !== 'hidden')
            .sort();
          const baselineLegs = (
            Array.isArray(recipient.visiblePaymentTypes) &&
            recipient.visiblePaymentTypes.length > 0
              ? [...recipient.visiblePaymentTypes]
              : ['upfront', 'trail']
          ).sort();
          if (JSON.stringify(desiredLegs) !== JSON.stringify(baselineLegs)) {
            payload.visibleLegs = desiredLegs;
            localPatch.visiblePaymentTypes = desiredLegs;
            hasChange = true;
          }
        }

        for (const [state, field] of [
          [upfrontState, 'hideGrossUpfrontCommission'],
          [trailState, 'hideGrossTrailCommission'],
        ] as const) {
          if (state === 'hidden') continue; // gross flag is moot while the leg is hidden
          const desiredHidden = state === 'payout_only';
          if (recipient[field] !== desiredHidden) {
            payload[field] = desiredHidden;
            localPatch[field] = desiredHidden;
            hasChange = true;
          }
        }

        if (!hasChange) continue;
        try {
          await toggleFn(payload);
          const applyPatch = <T extends { id: string }>(entries: T[]): T[] =>
            entries.map((entry) =>
              entry.id === recipient.id ? { ...entry, ...localPatch } : entry,
            );
          setBrokerRecipientUsers((previous) => applyPatch(previous));
          setAvailableAssistants((previous) => applyPatch(previous));
          setAvailableAnalysts((previous) => applyPatch(previous));
          setPendingRecipientLegs((previous) => {
            const { [recipient.id]: _cleared, ...rest } = previous;
            return rest;
          });
        } catch (err: any) {
          console.error(
            '[BrokerCommissionSettings] recipient visibility save failed:',
            recipient.id,
            err,
          );
          visibilityFailures.push(recipient.displayName);
        }
      }
      if (visibilityFailures.length > 0) {
        toast.error(
          `Commission settings saved, but visibility changes failed for ${visibilityFailures.join(', ')}. Their selections are still pending; press Save to retry.`,
        );
      }

      // Invalidate BigQuery-backed cache so monthly comms + broker loans
      // reflect the new trail-start filter without requiring a page reload.
      // The commission_config subcollection is already live via onSnapshot.
      if (trailStartMonthChanged) {
        await refetchBigQueryData();
        toast.success(
          normalizedTrailStartMonth
            ? `Saved. Trail commissions now visible from ${normalizedTrailStartMonth} onward across all pages.`
            : 'Saved. Trail start date cleared — full history now visible.'
        );
      } else {
        toast.success('Commission settings saved successfully');
      }
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Save error:', err);
      toast.error(err?.message || 'Failed to save commission settings');
    } finally {
      setSaving(false);
    }
  }, [companyId, brokerDocId, brokerIntroPct, companyIntroPct, trailBrokerIntroPct, trailCompanyIntroPct, upfrontSelfSourcedPct, trailSelfSourcedPct, trailVisibilityMode, threeTierActive, visibleUpfront, visibleTrail, baseSalary, revenueHurdle, hurdleClawbackBucket, hurdleYearEndRule, hurdleFiscalYearStartMonth, trailStartMonth, trailStartMonthFromHook, refetchBigQueryData, assistantSplits, creditAnalystSplits, commissionVisibilityRecipients, legStateFor]);

  const handleTagLoan = useCallback(async (loanId: string, introductionType: 'broker' | 'company' | 'self_sourced', lockedPct: number, customerName?: string, lender?: string, lockedTrailPct?: number) => {
    if (!companyId || !brokerDocId) return;
    if (!guardWrite('tagging a loan')) return;
    try {
      const tagFn = httpsCallable(functions, 'tagLoanIntroductionType');
      await tagFn({
        companyId,
        brokerDocId,
        loanId,
        introductionType,
        lockedPct,
        // PR C — lockedTrailPct is required for self_sourced and rejected
        // for broker / company by the backend. Send only when set + tier matches.
        ...(introductionType === 'self_sourced' && lockedTrailPct !== undefined && { lockedTrailPct }),
        customerName: customerName || null,
        lender: lender || null,
      });
      toast.success(`Loan ${loanId} tagged as ${introductionType} introduction (${lockedPct}%)`);
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Tag error:', err);
      toast.error(err?.message || 'Failed to tag loan');
    }
  }, [companyId, brokerDocId]);

  const handleBulkTag = useCallback(async (introductionType: 'broker' | 'company' | 'self_sourced') => {
    if (!companyId || !brokerDocId || selectedLoanIds.size === 0) return;
    if (!guardWrite('bulk tagging loans')) return;
    // Use current config's percentage for the bulk lock. self_sourced uses
    // the upfront pct from config + an explicit lockedTrailPct for the trail leg.
    const pctToLock =
      introductionType === 'broker'
        ? brokerIntroPct
        : introductionType === 'company'
          ? companyIntroPct
          : upfrontSelfSourcedPct;
    const trailPctToLock = introductionType === 'self_sourced' ? trailSelfSourcedPct : undefined;
    try {
      const bulkTagFn = httpsCallable(functions, 'bulkTagLoanIntroductionType');
      await bulkTagFn({
        companyId,
        brokerDocId,
        loanIds: Array.from(selectedLoanIds),
        introductionType,
        lockedPct: pctToLock,
        ...(trailPctToLock !== undefined && { lockedTrailPct: trailPctToLock }),
      });
      toast.success(`${selectedLoanIds.size} loans tagged as ${introductionType} introduction (${pctToLock}%)`);
      setSelectedLoanIds(new Set());
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Bulk tag error:', err);
      toast.error(err?.message || 'Failed to bulk tag loans');
    }
  }, [companyId, brokerDocId, selectedLoanIds, brokerIntroPct, companyIntroPct, upfrontSelfSourcedPct, trailSelfSourcedPct]);

  // PR 1A-v4 (2026-05-14): per-loan credit-analyst tagging. Untagged loans
  // pay no analyst (strict ownership semantics); tagged loans pay only the
  // named analyst their full per-payment-type share. Pass `null` to clear
  // the tag (= the loan stops paying any analyst). Backend validates that
  // the analyst is currently allocated on this broker's commission_config.
  const handleTagAnalyst = useCallback(
    async (loanId: string, creditAnalystId: string | null, customerName?: string, lender?: string) => {
      if (!companyId || !brokerDocId) return;
      if (!guardWrite('tagging analyst')) return;
      try {
        const tagFn = httpsCallable(functions, 'tagLoanCreditAnalyst');
        await tagFn({
          companyId,
          brokerDocId,
          loanId,
          creditAnalystId,
          customerName: customerName || null,
          lender: lender || null,
        });
        toast.success(
          creditAnalystId === null
            ? `Loan ${loanId} analyst tag cleared`
            : `Loan ${loanId} tagged to analyst`,
        );
      } catch (err: any) {
        console.error('[BrokerCommissionSettings] Tag analyst error:', err);
        toast.error(err?.message || 'Failed to tag loan analyst');
      }
    },
    [companyId, brokerDocId],
  );

  const handleBulkTagAnalyst = useCallback(
    async (creditAnalystId: string | null) => {
      if (!companyId || !brokerDocId || selectedLoanIds.size === 0) return;
      if (!guardWrite('bulk tagging analyst')) return;
      try {
        const fn = httpsCallable(functions, 'bulkTagLoanCreditAnalyst');
        await fn({
          companyId,
          brokerDocId,
          loanIds: Array.from(selectedLoanIds),
          creditAnalystId,
        });
        toast.success(
          creditAnalystId === null
            ? `${selectedLoanIds.size} loans untagged from analyst`
            : `${selectedLoanIds.size} loans tagged to analyst`,
        );
        setSelectedLoanIds(new Set());
      } catch (err: any) {
        console.error('[BrokerCommissionSettings] Bulk tag analyst error:', err);
        toast.error(err?.message || 'Failed to bulk tag loans');
      }
    },
    [companyId, brokerDocId, selectedLoanIds],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchOverview();
    } catch {
      // ignore
    } finally {
      setRefreshing(false);
    }
  }, [refetchOverview]);

  const resetAdjForm = useCallback(() => {
    setAdjForm({
      adjustmentType: '',
      loanId: '',
      customerName: '',
      lender: '',
      commissionDollars: '',
      gstDollars: '',
      gstAuto: true,
      periodMonth: currentMonth,
      settlementDate: '',
      loanAmountDollars: '',
      reason: '',
      introductionType: 'broker',
      introductionPct: brokerIntroPct,
    });
  }, [currentMonth, brokerIntroPct]);

  const handleAdjustmentSubmit = useCallback(async () => {
    if (!companyId || !brokerDocId) return;
    if (!guardWrite('creating an adjustment')) return;
    if (!adjForm.adjustmentType || !adjForm.loanId || !adjForm.commissionDollars || adjForm.reason.trim().length < 10) {
      toast.error('Please fill all required fields (reason must be 10+ characters)');
      return;
    }
    setAdjustmentSubmitting(true);
    try {
      const commissionCents = Math.round(parseFloat(adjForm.commissionDollars.replace(/,/g, '')) * 100);
      if (isNaN(commissionCents)) {
        toast.error('Invalid commission amount');
        setAdjustmentSubmitting(false);
        return;
      }
      let gstCents: number | null = null;
      if (adjForm.gstAuto) {
        gstCents = Math.round(commissionCents * 0.1);
      } else if (adjForm.gstDollars) {
        gstCents = Math.round(parseFloat(adjForm.gstDollars.replace(/,/g, '')) * 100);
        if (isNaN(gstCents)) gstCents = null;
      }
      let loanAmountCents: number | null = null;
      if (adjForm.loanAmountDollars) {
        loanAmountCents = Math.round(parseFloat(adjForm.loanAmountDollars.replace(/,/g, '')) * 100);
        if (isNaN(loanAmountCents)) loanAmountCents = null;
      }
      const createFn = httpsCallable(functions, 'createManualAdjustment');
      const result = await createFn({
        companyId,
        brokerDocId,
        loanId: adjForm.loanId.trim(),
        customerName: adjForm.customerName.trim() || null,
        lender: adjForm.lender.trim() || null,
        adjustmentType: adjForm.adjustmentType,
        commissionCents,
        gstCents,
        periodMonth: adjForm.periodMonth,
        settlementDate: adjForm.settlementDate || null,
        loanAmountCents,
        reason: adjForm.reason.trim(),
        introductionType: adjForm.introductionType,
        introductionPct: adjForm.introductionPct,
      });
      toast.success('Adjustment created successfully');
      setShowAdjustmentModal(false);
      resetAdjForm();
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Adjustment error:', err);
      toast.error(err?.message || 'Failed to create adjustment');
    } finally {
      setAdjustmentSubmitting(false);
    }
  }, [companyId, brokerDocId, adjForm, resetAdjForm]);

  const handleUpdateAdjustmentIntro = useCallback(async (adjustmentId: string, introType: 'broker' | 'company' | 'self_sourced', pct: number) => {
    if (!companyId || !brokerDocId) return;
    if (!guardWrite('updating an adjustment')) return;
    try {
      const updateFn = httpsCallable(functions, 'updateManualAdjustmentIntro');
      await updateFn({ companyId, brokerDocId, adjustmentId, introductionType: introType, introductionPct: pct });
      toast.success(`Adjustment updated to ${introType} introduction (${pct}%)`);
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Update adjustment intro error:', err);
      toast.error(err?.message || 'Failed to update adjustment');
    }
  }, [companyId, brokerDocId]);

  const handleVoidAdjustment = useCallback(async (adjustmentId: string) => {
    if (!companyId || !brokerDocId) return;
    if (!guardWrite('voiding an adjustment')) return;
    try {
      const voidFn = httpsCallable(functions, 'voidManualAdjustment');
      await voidFn({
        companyId,
        brokerDocId,
        adjustmentId,
        reason: 'Voided by administrator',
      });
      toast.success('Adjustment voided successfully');
    } catch (err: any) {
      console.error('[BrokerCommissionSettings] Void error:', err);
      toast.error(err?.message || 'Failed to void adjustment');
    } finally {
      setVoidConfirm({ isOpen: false, adjustmentId: '' });
    }
  }, [companyId, brokerDocId]);

  // Overall loading state
  const isLoading = brokersLoading || (selectedBroker === 'all' ? overviewLoading : configLoading);

  // ═══════════════════════════════════════════════════════════════════════════

  if (!companyId || companyData?.featureFlags?.commission_settings !== true) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-gray-500">This feature is not yet available for your company.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCog}
        title="Commission Settings"
        subtitle={subtitle}
        badge="NEW"
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        actions={
          <div className="flex items-center gap-3">
            <HelpButton pageKey="commissions" />
            {selectedBroker !== 'all' && (
              <button onClick={() => handleBrokerSwitch('all')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> All Brokers
              </button>
            )}
            <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold whitespace-nowrap">Broker:</label>
            <select value={selectedBroker} onChange={e => handleBrokerSwitch(e.target.value)} className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[220px]">
              <option value="all">All Brokers (Overview)</option>
              {brokerOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {selectedBroker !== 'all' && (userData?.role === 'super_admin' || userData?.role === 'administrator') && (
              <button
                onClick={() => setShowAdjustmentModal(true)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Read-only in view-as mode' : undefined}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-3.5 h-3.5" /> Add Adjustment
              </button>
            )}
            {selectedBroker !== 'all' && (
              <button
                onClick={handleSave}
                disabled={saving || isReadOnly}
                title={isReadOnly ? 'Read-only in view-as mode' : undefined}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{saving ? 'Saving...' : 'Save Settings'}
              </button>
            )}
          </div>
        }
      />

      {/* Loading State */}
      {isLoading && <LoadingState message={selectedBroker === 'all' ? 'Loading brokers overview...' : 'Loading broker configuration...'} />}

      {/* ═══ ALL BROKERS VIEW ═══ */}
      {!isLoading && selectedBroker === 'all' && (
        <div className="space-y-6">
          {/* Distinguish "load failed" from "no brokers in tenant" — prevents
              the silent-empty-state class of regression (e.g. the founder
              view-as / wrong-companyId bug fixed in this PR). */}
          {overviewError && overviewBrokers.length === 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              <span className="font-semibold">Couldn't load broker overview.</span>{' '}
              <span className="text-amber-200/80">{overviewError}</span>{' '}
              <button onClick={() => refetchOverview()} className="ml-2 underline hover:text-amber-100">Retry</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Total Broker Commissions (YTD)" value={formatCurrency(allBrokersTotals.commsCents)} accent="blue" detail={`Across ${filteredBrokersData.length} brokers — ${getCurrentFYLabel()} • ${COMMISSION_GST_BASIS}`} />
            <StatCard label="Total Base Salaries (Monthly)" value={formatCurrency(allBrokersTotals.baseSalaryCents)} accent="green" detail="Combined monthly payroll" />
            <StatCard label="Total Amt On Top of Base" value={formatCurrency(Math.abs(allBrokersTotals.amtOnTopCents))} accent="purple" valueColor={allBrokersTotals.amtOnTopCents >= 0 ? 'text-green-400' : 'text-red-400'} detail="YTD additional payments above base" />
            <StatCard label="Total Carry Over" value={allBrokersTotals.carryOverCents === 0 ? '$0' : `-${formatCurrency(Math.abs(allBrokersTotals.carryOverCents))}`} accent="red" valueColor={allBrokersTotals.carryOverCents < 0 ? 'text-red-400' : 'text-gray-300'} detail="Outstanding shortfalls across all brokers" />
          </div>

          {/* Template Filter */}
          {userVisibleTemplates?.length > 0 && (
            <div className="relative" ref={templateFilterRef}>
              <button
                onClick={() => setTemplateFilterOpen(!templateFilterOpen)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
              >
                <Filter className="w-4 h-4" />
                Loan Book Filter
                {selectedTemplateIds.size < (userVisibleTemplates?.length || 0) && (
                  <span className="px-1.5 py-0.5 text-[0.625rem] font-bold rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {selectedTemplateIds.size}/{userVisibleTemplates.length}
                  </span>
                )}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${templateFilterOpen ? 'rotate-180' : ''}`} />
              </button>
              {templateFilterOpen && (
                <div className="absolute z-20 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 space-y-1">
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-700">
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Loan Books</span>
                    <button
                      onClick={() => {
                        let next: Set<string>;
                        if (selectedTemplateIds.size === userVisibleTemplates.length) {
                          next = new Set();
                        } else {
                          next = new Set(userVisibleTemplates.map((t: any) => t.id));
                        }
                        setSelectedTemplateIds(next);
                        if (templateFilterKey) localStorage.setItem(templateFilterKey, JSON.stringify([...next]));
                      }}
                      className="text-[0.625rem] text-blue-400 hover:text-blue-300 font-medium"
                    >
                      {selectedTemplateIds.size === userVisibleTemplates.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  {userVisibleTemplates.map((t: any) => (
                    <label key={t.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-700/50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedTemplateIds.has(t.id)}
                        onChange={() => toggleTemplateFilter(t.id)}
                        className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-gray-300">{t.name || t.templateName || t.id}</span>
                    </label>
                  ))}
                  <p className="text-[0.625rem] text-gray-500 mt-2 pt-2 border-t border-gray-700">
                    Filter brokers by their loan book. Brokers with no commission data yet will appear in all loan books.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* All Brokers Summary Table — Grouped by Office */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div><h3 className="text-lg font-bold text-white">All Brokers — Commission & Pay Overview</h3><p className="text-sm text-gray-400 mt-1">Summary of commission settings, pay reconciliation, and loan status per broker</p></div>
              <div className="flex items-center gap-3">
                {hiddenBrokerIds.size > 0 && (
                  <button
                    onClick={() => setShowHidden(!showHidden)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${showHidden ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:text-gray-300'}`}
                  >
                    {showHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {hiddenBrokerIds.size} hidden
                  </button>
                )}
                <select value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); if (periodFilterKey) localStorage.setItem(periodFilterKey, e.target.value); }} className="bg-gray-700 border border-gray-600 text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="current_month">Current Month</option><option value="last_3">Last 3 Months</option><option value="ytd">FY Year-to-Date</option><option value="last_12">Last 12 Months</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-5 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Broker</th>
                    {['Base Salary', 'Broker %', 'Company %', 'Broker Share (est.)', 'Total Paid', 'Amt on Top of Base', 'Carry Over'].map(h => <th key={h} className="px-5 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}
                    <th className="px-5 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Visibility</th>
                    <th className="px-5 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">Untagged</th>
                    <th className="px-5 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {officeGroups.length === 0 && (
                    <tr><td colSpan={11} className="px-5 py-8 text-center text-gray-500">No active brokers found.</td></tr>
                  )}
                  {officeGroups.map(group => (
                    <React.Fragment key={group.officeId}>
                      {/* Office group header row */}
                      <tr
                        className="bg-gray-700/30 border-b border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors"
                        onClick={() => toggleOfficeGroup(group.officeId)}
                      >
                        <td className="px-5 py-3" colSpan={11}>
                          <div className="flex items-center gap-2">
                            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedOffices.has(group.officeId) ? 'rotate-90' : ''}`} />
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="text-white font-bold text-sm">{group.officeName}</span>
                            <span className="text-gray-500 text-xs">({group.brokers.length} {group.brokers.length === 1 ? 'broker' : 'brokers'})</span>
                          </div>
                        </td>
                      </tr>
                      {/* Broker rows within this office group */}
                      {expandedOffices.has(group.officeId) && group.brokers.map(b => (
                        <tr key={b.id} className="border-b border-gray-800 hover:bg-vlt-dark transition-colors">
                          <td className="px-5 py-4 pl-12"><div className="flex items-center gap-2"><div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${b.avatarColor}`}>{b.initials}</div><span className="text-white font-medium">{b.name}</span></div></td>
                          <td className="px-5 py-4 text-right text-gray-300 font-medium">{formatCurrency(b.baseSalaryCents)}</td>
                          <td className="px-5 py-4 text-right text-blue-400 font-semibold">{b.isConfigured ? `${b.brokerIntroPct}%` : '—'}</td>
                          <td className="px-5 py-4 text-right text-purple-400 font-semibold">{b.isConfigured ? `${b.companyIntroPct}%` : '—'}</td>
                          <td className="px-5 py-4 text-right font-semibold">
                            {b.isConfigured
                              ? (
                                <>
                                  <span className="text-green-400">{formatCurrency(b.commsCents)}</span>
                                  {b.allocatedCents > 0 && (
                                    <span
                                      className="block text-[0.625rem] font-normal text-amber-400/90"
                                      title={`Share before allocations ${formatCurrency(b.grossShareCents)}, less ${formatCurrency(b.allocatedCents)} allocated to assistants or credit analysts.`}
                                    >
                                      {formatCurrency(b.grossShareCents)} less {formatCurrency(b.allocatedCents)} allocated
                                    </span>
                                  )}
                                </>
                              )
                              : <span className="text-gray-500 font-normal italic" title="No commission split configured for this broker yet">Not configured</span>}
                          </td>
                          <td className="px-5 py-4 text-right text-white font-semibold">{formatCurrency(b.totalPaidCents)}</td>
                          <td className={`px-5 py-4 text-right font-bold ${b.amtOnTopCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{b.amtOnTopCents < 0 ? `-${formatCurrency(Math.abs(b.amtOnTopCents))}` : formatCurrency(b.amtOnTopCents)}</td>
                          <td className={`px-5 py-4 text-right font-bold ${b.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{b.carryOverCents < 0 ? `-${formatCurrency(Math.abs(b.carryOverCents))}` : '$0'}</td>
                          <td className="px-5 py-4 text-center"><div className="flex gap-1 justify-center">{b.visibleUpfront && <span className="px-1.5 py-0.5 text-[0.625rem] rounded bg-blue-500/20 text-blue-300">UF</span>}{b.visibleTrail && <span className="px-1.5 py-0.5 text-[0.625rem] rounded bg-green-500/20 text-green-300">TR</span>}</div></td>
                          <td className="px-5 py-4 text-right">{b.untaggedCount > 0 ? <span className="text-amber-400 font-semibold">{b.untaggedCount}</span> : <span className="text-gray-500">0</span>}</td>
                          <td className="px-5 py-4 text-center"><div className="flex items-center gap-1.5 justify-center"><button onClick={() => handleBrokerSwitch(b.id)} className="px-2.5 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors">View</button><button onClick={() => toggleHideBroker(b.id)} disabled={isReadOnly} className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title={isReadOnly ? 'Read-only in view-as mode' : (hiddenBrokerIds.has(b.id) ? 'Show in overview' : 'Hide from overview')}>{hiddenBrokerIds.has(b.id) ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button></div></td>
                        </tr>
                      ))}
                      {/* Office subtotal row */}
                      {expandedOffices.has(group.officeId) && group.brokers.length > 1 && (() => {
                        const officeComms = group.brokers.reduce((s, b) => s + b.commsCents, 0);
                        const officeSalary = group.brokers.reduce((s, b) => s + b.baseSalaryCents, 0);
                        const officePaid = group.brokers.reduce((s, b) => s + b.totalPaidCents, 0);
                        const officeOnTop = group.brokers.reduce((s, b) => s + b.amtOnTopCents, 0);
                        const officeCarry = group.brokers.reduce((s, b) => s + b.carryOverCents, 0);
                        const officeUntagged = group.brokers.reduce((s, b) => s + b.untaggedCount, 0);
                        return (
                          <tr className="bg-gray-700/20 border-b border-gray-700">
                            <td className="px-5 py-3 pl-12 text-gray-300 font-semibold text-sm">{group.officeName} Total</td>
                            <td className="px-5 py-3 text-right text-gray-300 font-semibold">{formatCurrency(officeSalary)}</td>
                            <td className="px-5 py-3" colSpan={2} />
                            <td className="px-5 py-3 text-right text-green-400 font-semibold">{formatCurrency(officeComms)}</td>
                            <td className="px-5 py-3 text-right text-white font-semibold">{formatCurrency(officePaid)}</td>
                            <td className={`px-5 py-3 text-right font-semibold ${officeOnTop >= 0 ? 'text-green-400' : 'text-red-400'}`}>{officeOnTop < 0 ? `-${formatCurrency(Math.abs(officeOnTop))}` : formatCurrency(officeOnTop)}</td>
                            <td className={`px-5 py-3 text-right font-semibold ${officeCarry < 0 ? 'text-red-400' : 'text-gray-500'}`}>{officeCarry < 0 ? `-${formatCurrency(Math.abs(officeCarry))}` : '$0'}</td>
                            <td className="px-5 py-3" />
                            <td className="px-5 py-3 text-right text-amber-400 font-semibold">{officeUntagged > 0 ? officeUntagged : ''}</td>
                            <td className="px-5 py-3" />
                          </tr>
                        );
                      })()}
                    </React.Fragment>
                  ))}
                  {filteredBrokersData.length > 0 && (
                    <tr className="bg-blue-500/5 border-t border-gray-600">
                      <td className="px-5 py-4 text-white font-bold">Total ({filteredBrokersData.length} brokers)</td>
                      <td className="px-5 py-4 text-right text-gray-300 font-bold">{formatCurrency(allBrokersTotals.baseSalaryCents)}</td><td className="px-5 py-4" colSpan={2} />
                      <td className="px-5 py-4 text-right text-green-400 font-bold text-lg">{formatCurrency(allBrokersTotals.commsCents)}</td>
                      <td className="px-5 py-4 text-right text-white font-bold text-lg">{formatCurrency(allBrokersTotals.totalPaidCents)}</td>
                      <td className={`px-5 py-4 text-right font-bold ${allBrokersTotals.amtOnTopCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(allBrokersTotals.amtOnTopCents)}</td>
                      <td className={`px-5 py-4 text-right font-bold ${allBrokersTotals.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{allBrokersTotals.carryOverCents < 0 ? `-${formatCurrency(Math.abs(allBrokersTotals.carryOverCents))}` : '$0'}</td>
                      <td className="px-5 py-4" /><td className="px-5 py-4 text-right text-amber-400 font-bold">{allBrokersTotals.untaggedCount}</td><td className="px-5 py-4" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PR 1A-v3 (2026-05-13): Broker Assistants — cross-broker rollup.
              Shows when at least one assistant has a non-zero allocation
              across the visible brokers. Read-only summary; clicking a row
              navigates to the first funding broker for full editing. */}
          {overviewAssistantRollup && overviewAssistantRollup.length > 0 && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Broker Assistants — Allocations Across Brokers</h3>
                <p className="text-sm text-gray-400 mt-1">Read-only summary. Click a row to edit allocations on the broker's commission settings page.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/50 text-gray-400 uppercase text-[0.625rem] tracking-wide">
                    <tr>
                      <th className="px-5 py-3 text-left">Assistant</th>
                      <th className="px-5 py-3 text-left">Funded By</th>
                      <th className="px-5 py-3 text-right">Total Share</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {overviewAssistantRollup.map((row) => (
                      <tr
                        key={row.assistantId}
                        className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                        onClick={() => row.brokers[0] && handleBrokerSwitch(row.brokers[0].brokerDocId)}
                      >
                        <td className="px-5 py-3 font-semibold text-white">{row.displayName}</td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {row.brokers.map((b) => (
                              <span
                                key={b.brokerDocId}
                                className="px-2 py-0.5 text-[0.625rem] rounded bg-gray-700 text-gray-300 border border-gray-600"
                              >
                                {b.brokerName} ({b.sharePct}%)
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-blue-300">{row.totalSharePct}%</td>
                        <td className="px-5 py-3 text-right text-gray-500 text-xs">View →</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PR 1A-v3 (2026-05-13): Credit Analysts — cross-broker rollup
              with per-payment-type split (upfront vs trail) reflecting the
              PR 1A-v3 schema asymmetry. */}
          {overviewAnalystRollup && overviewAnalystRollup.length > 0 && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Credit Analysts — Allocations Across Brokers</h3>
                <p className="text-sm text-gray-400 mt-1">Read-only summary. Performance targets for analysts are configured on each broker's Performance Targets tab. Click a row to edit on the broker's commission settings page.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/50 text-gray-400 uppercase text-[0.625rem] tracking-wide">
                    <tr>
                      <th className="px-5 py-3 text-left">Analyst</th>
                      <th className="px-5 py-3 text-left">Funded By</th>
                      <th className="px-5 py-3 text-right">Total Upfront</th>
                      <th className="px-5 py-3 text-right">Total Trail</th>
                      {/* PR 1A-v4 (2026-05-14): Active Targets column —
                          surfaces each analyst's active targets across all
                          funding brokers so Dieter doesn't have to drill into
                          each broker to know what they're measured against. */}
                      <th className="px-5 py-3 text-left">Active Targets</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {overviewAnalystRollup.map((row) => (
                      <tr
                        key={row.analystId}
                        className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                        onClick={() => row.brokers[0] && handleBrokerSwitch(row.brokers[0].brokerDocId)}
                      >
                        <td className="px-5 py-3 font-semibold text-white">
                          <span>{row.displayName}</span>
                          {!row.hasLogin && (
                            <span className="ml-2 px-1.5 py-0.5 text-[0.625rem] font-bold rounded bg-gray-700 text-gray-400 border border-gray-600">
                              NO LOGIN
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {row.brokers.map((b) => (
                              <span
                                key={b.brokerDocId}
                                className="px-2 py-0.5 text-[0.625rem] rounded bg-gray-700 text-gray-300 border border-gray-600"
                                title={`${b.brokerName}: ${b.upfrontSharePct}% upfront / ${b.trailSharePct}% trail`}
                              >
                                {b.brokerName} ({b.upfrontSharePct}/{b.trailSharePct})
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-blue-300">{row.totalUpfrontSharePct}%</td>
                        <td className="px-5 py-3 text-right font-bold text-blue-300">{row.totalTrailSharePct}%</td>
                        <td className="px-5 py-3">
                          {(row.activeTargets ?? []).length === 0 ? (
                            <span className="text-[0.625rem] text-gray-500 italic">None</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {(row.activeTargets ?? []).map((t) => {
                                // PR 1A-v4.1 (2026-05-14): progress fields
                                // populated by computeAnalystTargetProgress.
                                // Colour bands: red <25, amber <100, green
                                // met. Threshold compact ($K/$M, three
                                // significant figures) for rollup compactness.
                                const fmtCompact = formatCompactCurrency;
                                const pct = t.percentComplete;
                                const tone =
                                  pct >= 100
                                    ? 'bg-green-500/15 text-green-300 border-green-500/30'
                                    : pct >= 25
                                      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                      : 'bg-rose-500/10 text-rose-300/90 border-rose-500/30';
                                return (
                                  <span
                                    key={t.targetId}
                                    className={`px-2 py-0.5 text-[0.625rem] rounded border inline-block w-fit ${tone}`}
                                    title={`${t.brokerName} • ${t.type} • ${t.period} • current $${(t.currentValueCents / 100).toLocaleString('en-AU')} of threshold $${(t.thresholdCents / 100).toLocaleString('en-AU')} (${pct}%)`}
                                  >
                                    {t.label}{' '}
                                    <span className="opacity-80">
                                      {fmtCompact(t.currentValueCents)}/{fmtCompact(t.thresholdCents)} ({pct}%)
                                    </span>{' '}
                                    <span className="opacity-60">({t.brokerName})</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right text-gray-500 text-xs">View →</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB SWITCHER (individual broker only) ═══ */}
      {!isLoading && selectedBroker !== 'all' && (
        <div className="flex items-center border-b border-gray-700">
          <button
            onClick={() => {
              setActiveTab('commission');
              const next = new URLSearchParams(searchParams);
              next.delete('tab');
              setSearchParams(next, { replace: true });
            }}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'commission'
                ? 'border-blue-500 text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Commission & Splits
          </button>
          <button
            onClick={() => {
              setActiveTab('targets');
              const next = new URLSearchParams(searchParams);
              next.set('tab', 'targets');
              setSearchParams(next, { replace: true });
            }}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'targets'
                ? 'border-blue-500 text-white'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Performance Targets
          </button>
        </div>
      )}

      {/* ═══ INDIVIDUAL BROKER VIEW — Performance Targets tab ═══ */}
      {!isLoading && selectedBroker !== 'all' && activeTab === 'targets' && (
        <PerformanceTargetsTab
          companyId={companyId}
          selectedBrokerDocId={selectedBroker}
          loanBooks={loanBooks}
          assistants={Object.entries(assistantSplits).map(([id, s]) => ({
            id,
            displayName: s.displayName,
            sharePct: s.sharePct,
          }))}
          analysts={Object.entries(creditAnalystSplits).map(([id, s]) => ({
            id,
            displayName: s.displayName,
            upfrontSharePct: s.upfrontSharePct,
            trailSharePct: s.trailSharePct,
          }))}
          isReadOnly={isReadOnly}
        />
      )}

      {/* ═══ INDIVIDUAL BROKER VIEW — Commission & Splits tab ═══ */}
      {!isLoading && selectedBroker !== 'all' && activeTab === 'commission' && (
        <div className="space-y-6">
          {/* Untagged Alert */}
          {untaggedCount > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1"><h4 className="text-amber-400 font-semibold text-sm">{untaggedCount} loans have no introduction type set</h4><p className="text-amber-400/70 text-xs mt-0.5">Commission splits cannot be calculated for untagged loans. The Company Introduction percentage ({companyIntroPct}%) will be applied until they are tagged.</p></div>
              <button onClick={() => document.getElementById('loan-tags')?.scrollIntoView({ behavior: 'smooth' })} className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 transition-colors">Review & Tag</button>
            </div>
          )}

          {/* Data currency. Every figure on this page is valued at the broker's
              CURRENT saved split (configs restate history, founder-accepted
              2026-08-11) and stops at the newest period month loaded. */}
          <p className="text-xs text-gray-400">
            {latestLoadedMonth
              ? <>Commission data current to <span className="text-gray-200 font-semibold">{monthLabel(latestLoadedMonth)}</span></>
              : 'No commission data loaded yet'}
            {' · '}broker share valued at the current saved split
            {tagsHasMore && <span className="text-amber-400"> · loading loan tags…</span>}
          </p>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCardWithIcon label="Broker Commission (FY to date)" value={notConfigured ? 'Not configured' : reconYtdTotals.commsCents ? formatCurrencyWithCents(reconYtdTotals.commsCents) : '$0.00'} accent="blue" detail={notConfigured ? 'Save a commission split to value this broker' : `${fiscalYearToDateDetail(currentFY, reconYtdTotals)} · ${COMMISSION_GST_BASIS}`} valueColor={notConfigured ? 'text-gray-400' : undefined} icon={<DollarSign className="w-5 h-5" />} iconBg="rgba(59,130,246,0.3)" iconColor="#60a5fa" />
            <StatCardWithIcon label="Base Salary (Monthly)" value={currentConfig?.baseSalaryCents ? formatCurrencyWithCents(currentConfig.baseSalaryCents) : '$0.00'} accent="green" detail={currentConfig ? `Config saved ${formatDateShort(currentConfig.effectiveFrom)}` : 'Not configured'} icon={<CreditCard className="w-5 h-5" />} iconBg="rgba(34,197,94,0.3)" iconColor="#4ade80" />
            <StatCardWithIcon label="Carry Over Balance" value={notConfigured ? 'Not configured' : reconYtdTotals.carryOverCents ? formatCurrencyWithCents(reconYtdTotals.carryOverCents) : '$0.00'} accent="purple" detail={notConfigured ? 'No split saved' : reconYtdTotals.carryOverCents < 0 ? 'Shortfall carried to next month' : 'No carry over'} valueColor={notConfigured ? 'text-gray-400' : reconYtdTotals.carryOverCents < 0 ? 'text-red-400' : undefined} icon={<DollarSign className="w-5 h-5" />} iconBg="rgba(139,92,246,0.3)" iconColor="#a78bfa" />
            <StatCardWithIcon label="Untagged Loans" value={tagsHasMore ? '…' : String(untaggedCount)} accent="amber" detail={tagsHasMore ? 'Loading all loan tags' : `Defaulting to Company Intro (${companyIntroPct}%)`} valueColor="text-amber-400" icon={<Tag className="w-5 h-5" />} iconBg="rgba(245,158,11,0.3)" iconColor="#fbbf24" />
          </div>

          {/* Reconciliation Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div><h3 className="text-lg font-bold text-white">Commission vs Base Salary Reconciliation</h3><p className="text-sm text-gray-400 mt-1">Monthly draw against commission — base salary is the guaranteed minimum. Shortfalls carry over.</p></div>
              <div className="flex items-center gap-2"><span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Legend:</span><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-green-500/10 text-green-400">Excess = paid on top</span><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-red-500/10 text-red-400">Shortfall = carry over</span></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700"><tr>{['Month'].map(h => <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}{['Broker Comms', 'Base Salary', 'Total Paid', 'Amt On Top of Base', 'Carry Over'].map(h => <th key={h} className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}<th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Note</th></tr></thead>
                <tbody>
                  {reconciliation.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">No reconciliation data available yet. Commission data will appear once uploaded.</td></tr>
                  )}
                  {notConfigured && reconciliation.length > 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500 italic">Not configured. Save a commission split above to reconcile this broker against base salary.</td></tr>
                  )}
                  {!notConfigured && reconYearGroups.map(yg => {
                    const yearTotal = yg.months.reduce((s, r) => s + r.brokerCommsCents, 0);
                    const isExpanded = expandedReconYears.has(yg.year);
                    return (
                      <React.Fragment key={yg.year}>
                        <tr className="bg-gray-700/30 border-b border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors" onClick={() => setExpandedReconYears(prev => { const next = new Set(prev); if (next.has(yg.year)) next.delete(yg.year); else next.add(yg.year); return next; })}>
                          <td className="px-6 py-3 text-white font-bold"><span className="text-gray-500 mr-2">{isExpanded ? '▾' : '▸'}</span>{yg.label}<span className="ml-2 text-xs font-normal text-gray-500">{yg.rangeLabel}</span></td>
                          <td className="px-6 py-3 text-right text-green-400 font-bold">{notConfigured ? <span className="text-gray-500 italic font-normal">Not configured</span> : formatCurrencyWithCents(yearTotal)}</td>
                          <td className="px-6 py-3 text-right text-gray-500 text-xs">{yg.months.length} month{yg.months.length === 1 ? '' : 's'} loaded</td>
                          <td colSpan={4}></td>
                        </tr>
                        {isExpanded && yg.months.map(r => (
                          <tr key={r.month} className="border-b border-gray-800 hover:bg-vlt-dark transition-colors">
                            <td className="px-6 py-3.5 pl-10 text-white font-medium">{formatMonthLabel(r.month)}</td>
                            <td className={`px-6 py-3.5 text-right font-semibold ${r.brokerCommsCents >= r.baseSalaryCents ? 'text-green-400' : 'text-amber-400'}`}>{formatCurrencyWithCents(r.brokerCommsCents)}</td>
                            <td className="px-6 py-3.5 text-right text-gray-300">{formatCurrencyWithCents(r.baseSalaryCents)}</td>
                            <td className="px-6 py-3.5 text-right text-white font-bold">{formatCurrencyWithCents(r.totalPaidCents)}</td>
                            <td className={`px-6 py-3.5 text-right font-bold ${r.amtOnTopOfBaseCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrencyWithCents(r.amtOnTopOfBaseCents)}</td>
                            <td className={`px-6 py-3.5 text-right font-bold ${r.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{r.carryOverCents === 0 ? '$0.00' : formatCurrencyWithCents(r.carryOverCents)}</td>
                            <td className={`px-6 py-3.5 text-xs ${r.note.includes('Shortfall') ? 'text-red-400' : 'text-gray-500'}`}>{r.note}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {reconciliation.length > 0 && !notConfigured && (
                    <tr className="bg-blue-500/5 border-t border-gray-600">
                      <td className="px-6 py-4 text-white font-bold">{fiscalYearLabel(currentFY)} to date<span className="ml-2 text-xs font-normal text-gray-500">{fiscalYearToDateDetail(currentFY, reconYtdTotals).replace(`${fiscalYearLabel(currentFY)} · `, '')}</span></td>
                      <td className="px-6 py-4 text-right text-green-400 font-bold text-lg">{notConfigured ? <span className="text-gray-500 italic text-sm font-normal">Not configured</span> : formatCurrencyWithCents(reconYtdTotals.commsCents)}</td>
                      <td className="px-6 py-4 text-right text-gray-300 font-bold">{formatCurrencyWithCents(reconYtdTotals.baseSalaryCents)}</td>
                      <td className="px-6 py-4 text-right text-white font-bold text-lg">{formatCurrencyWithCents(reconYtdTotals.totalPaidCents)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${reconYtdTotals.amtOnTopCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrencyWithCents(reconYtdTotals.amtOnTopCents)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${reconYtdTotals.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{formatCurrencyWithCents(reconYtdTotals.carryOverCents)}</td>
                      <td className="px-6 py-4 text-xs text-gray-400">Net amount to be paid on top of base</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {hurdleSchedule.length > 0 && (
            <RevenueHurdleSchedule schedule={hurdleSchedule} shareBasisLabel="broker net share" />
          )}

          {/* PR C — Commission Structure toggle (admin-only).
              Activates the 3-tier UI for THIS broker. Other brokers in the
              same company never see anything 3-tier-related in their own
              broker view, preventing "why don't I have this too?" questions. */}
          {isAdminViewer && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">Commission Structure</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {threeTierActive
                        ? '3-tier (Internal Referral / Repeat / Self-Sourced) — visible to this broker only'
                        : '2-tier (Broker / Company introduction) — standard'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStructureModal(true)}
                  disabled={structureSaving || isReadOnly}
                  title={isReadOnly ? 'Read-only in view-as mode' : undefined}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    threeTierActive
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {threeTierActive ? 'Activated' : 'Activate 3-Tier'}
                </button>
              </div>
            </div>
          )}

          {/* Commission Split Percentages — 3-tier panel (when activated) or
              legacy 2-row panel (default). Same data is hydrated into both
              variants from currentConfig so toggling structure mid-session
              doesn't lose admin input. */}
          {threeTierActive ? (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Client Group Commission Tiers</h3>
                    <p className="text-sm text-gray-400 mt-0.5">Per-tier upfront and trail split based on who sourced the client</p>
                  </div>
                </div>
                {currentConfig && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-xs font-medium text-green-400">Settings active</span>
                  </div>
                )}
              </div>
              <div className="p-6 space-y-5">
                {([
                  {
                    key: 'company', label: 'Internal Referral', sub: 'Company introduced the client to the broker',
                    Icon: Building2, iconBg: 'bg-purple-500/20', iconColor: 'text-purple-400',
                    upfront: companyIntroPct, setUpfront: setCompanyIntroPct,
                    trail: trailCompanyIntroPct, setTrail: setTrailCompanyIntroPct,
                  },
                  {
                    key: 'broker', label: 'Repeat / Existing Client', sub: 'Broker re-introduced a known client',
                    Icon: User, iconBg: 'bg-blue-500/20', iconColor: 'text-blue-400',
                    upfront: brokerIntroPct, setUpfront: setBrokerIntroPct,
                    trail: trailBrokerIntroPct, setTrail: setTrailBrokerIntroPct,
                  },
                  {
                    key: 'self_sourced', label: 'Self-Sourced Business', sub: 'Broker independently sourced the client',
                    Icon: Briefcase, iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-400',
                    upfront: upfrontSelfSourcedPct, setUpfront: setUpfrontSelfSourcedPct,
                    trail: trailSelfSourcedPct, setTrail: setTrailSelfSourcedPct,
                  },
                ] as const).map(({ key, label, sub, Icon, iconBg, iconColor, upfront, setUpfront, trail, setTrail }) => (
                  <div key={key} className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center">
                    <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <HandCoins className="w-4 h-4 text-amber-400" />
                      <input type="number" value={upfront} onChange={e => setUpfront(Number(e.target.value))}
                        min={0} max={100}
                        className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <span className="text-gray-400 text-sm">%</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1 w-14">upfront</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-cyan-400" />
                      <input type="number" value={trail} onChange={e => setTrail(Number(e.target.value))}
                        min={0} max={100}
                        className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <span className="text-gray-400 text-sm">%</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1 w-14">trail</span>
                    </div>
                  </div>
                ))}
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-xs text-blue-400/80">
                    <strong>Per-loan tagging.</strong> Each loan must be tagged with its tier in the Loan Tagger below.
                    Untagged loans default to <strong>Internal Referral</strong> (the lower split) — a conservative default.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Legacy 2-row split card — unchanged for non-3-tier brokers */
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div><h3 className="text-lg font-bold text-white">Commission Split Percentages</h3><p className="text-sm text-gray-400 mt-1">Set the broker's share based on who introduced the customer</p></div>
                {currentConfig && <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /><span className="text-xs font-medium text-green-400">Settings active</span></div>}
              </div>
              <div className="p-6 space-y-8">
                {/* Broker Intro */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center"><User className="w-4 h-4 text-blue-400" /></div><div><label className="text-sm font-semibold text-white">Broker Introduction</label><p className="text-xs text-gray-500">When the broker introduced the customer to the mortgage company</p></div></div>
                    <div className="flex items-center gap-3"><input type="number" value={brokerIntroPct} onChange={e => setBrokerIntroPct(Number(e.target.value))} min={0} max={100} className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" /><span className="text-gray-400 text-lg font-semibold">%</span></div>
                  </div>
                  <div className="flex items-center gap-4"><div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-blue-400" style={{ width: `${brokerIntroPct}%` }} /></div><div className="flex items-center gap-2 text-xs w-64 justify-end"><span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-semibold">Broker {brokerIntroPct}%</span><span className="text-gray-600">|</span><span className="px-2 py-0.5 rounded bg-gray-600/50 text-gray-400 font-semibold">Company {100 - brokerIntroPct}%</span></div></div>
                </div>
                <div className="h-px bg-gray-700" />
                {/* Company Intro */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center"><Building2 className="w-4 h-4 text-purple-400" /></div><div><label className="text-sm font-semibold text-white">Mortgage Company Introduction</label><p className="text-xs text-gray-500">When the mortgage company introduced the customer to the broker</p></div></div>
                    <div className="flex items-center gap-3"><input type="number" value={companyIntroPct} onChange={e => setCompanyIntroPct(Number(e.target.value))} min={0} max={100} className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" /><span className="text-gray-400 text-lg font-semibold">%</span></div>
                  </div>
                  <div className="flex items-center gap-4"><div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-400" style={{ width: `${companyIntroPct}%` }} /></div><div className="flex items-center gap-2 text-xs w-64 justify-end"><span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-semibold">Broker {companyIntroPct}%</span><span className="text-gray-600">|</span><span className="px-2 py-0.5 rounded bg-gray-600/50 text-gray-400 font-semibold">Company {100 - companyIntroPct}%</span></div></div>
                </div>
              </div>
            </div>
          )}

          {/* PR C — Trail Visibility card (3-tier only).
              Per-broker filter that hides ALL trail rows from the broker's
              own view EXCEPT loans they've tagged as self_sourced. Admins
              always see the full books regardless of this setting. */}
          {threeTierActive && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Trail Commission Visibility</h3>
                <p className="text-sm text-gray-400 mt-1">Which trail commission rows appear in the broker's own view</p>
              </div>
              <div className="p-6 space-y-3">
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${trailVisibilityMode === 'all' ? 'bg-blue-500/10 border-blue-500/40' : 'bg-gray-900/40 border-gray-700 hover:border-gray-600'}`}>
                  <input
                    type="radio"
                    checked={trailVisibilityMode === 'all'}
                    onChange={() => setTrailVisibilityMode('all')}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">All loans</p>
                    <p className="text-xs text-gray-400 mt-0.5">Default. Broker sees trail commission for every loan they're attributed to.</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${trailVisibilityMode === 'self_sourced_only' ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-gray-900/40 border-gray-700 hover:border-gray-600'}`}>
                  <input
                    type="radio"
                    checked={trailVisibilityMode === 'self_sourced_only'}
                    onChange={() => setTrailVisibilityMode('self_sourced_only')}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">Self-sourced loans only</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Broker sees trail commission <strong>only</strong> for loans tagged "Self-Sourced Business".
                      Trail for Internal Referral / Repeat / untagged loans is hidden from the broker.
                      Admins always see the full books for reconciliation.
                    </p>
                  </div>
                </label>
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-xs text-blue-400/80">
                    <strong>App-wide enforcement.</strong> Tag-based filter is applied in BigQuery for all broker-scoped commission queries.
                    Admin bypass: super_admin and administrator viewers see the unfiltered books regardless of this setting.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Broker-level settings: trail cutoff and base salary.

              ONE grid holds BOTH cards. Base Salary used to sit in its own
              `md:grid-cols-2` as the only child, which reserved a second column and
              left it permanently empty, while this card ran full width and stretched
              the month picker across the whole page.

              `items-start` matters: without it the shorter card stretches to match
              Salary History, which grows on every salary change, so the dead space
              comes back vertically instead of horizontally. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Trail Commission Start Date — `relative` so the MonthPicker
                popover anchors here; NO `overflow-hidden` so the popover can
                extend below the card without being clipped by the parent. */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg relative">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Trail Commission Start Date</h3>
                <p className="text-sm text-gray-400 mt-1">Trail commissions before this date are hidden from this broker across all pages</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 mt-6 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-400 mb-2 block">Start Month</label>
                    <MonthPicker
                      value={trailStartMonth}
                      onChange={setTrailStartMonth}
                      placeholder="Select trail start month"
                    />
                  </div>
                </div>
                {trailStartMonth && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/30">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-xs font-semibold text-green-400">Active: Trail visible from {(() => { const [y, m] = trailStartMonth.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${months[parseInt(m,10)-1]} ${y}`; })()} onward</span>
                  </div>
                )}
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-xs text-blue-400/80"><strong>App-wide enforcement:</strong> This date filters trail, arrears, and discharged commissions across the dashboard, reports, and all pages. Upfront commissions are never filtered.</p>
                </div>
              </div>
            </div>

            {/* Base Salary */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700"><h3 className="text-lg font-bold text-white">Base Salary</h3><p className="text-sm text-gray-400 mt-1">Monthly base salary (point-in-time tracked)</p></div>
              <div className="p-6 space-y-4">
                <div><label className="text-sm font-medium text-gray-400 mb-2 block">Monthly Base Salary (AUD)</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span><input type="text" value={baseSalary} onChange={e => setBaseSalary(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-4 py-3 text-white text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div><p className="text-xs text-gray-500 mt-2">Changes create a new effective period. Previous salary is preserved in history.</p></div>
                <div className="h-px bg-gray-700 my-2" />
                {/* Monthly revenue hurdle (2026-10-06). Mutually exclusive with a
                    base salary: one is a floor, the other scales the payout. */}
                <div data-testid="revenue-hurdle-config">
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">Monthly Revenue Hurdle</h4>
                  <p className="text-xs text-gray-500 mb-3">The revenue a broker must clear each month before the split rate applies. Leave blank for no hurdle. Cannot be combined with a base salary.</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Monthly Revenue Hurdle (AUD)</label>
                      <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span><input type="text" value={revenueHurdle} onChange={e => setRevenueHurdle(e.target.value)} placeholder="No hurdle" aria-label="Monthly revenue hurdle" className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-4 py-3 text-white text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" /></div>
                      <p className="text-xs text-gray-500 mt-2">Attributed revenue is upfront on every deal plus trail on the broker's own deals, less clawbacks and referrer fees.</p>
                    </div>
                    {revenueHurdle.trim() !== '' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1 block">Clawbacks count against</label>
                          <select value={hurdleClawbackBucket} onChange={e => setHurdleClawbackBucket(e.target.value as HurdleClawbackBucket)} aria-label="Hurdle clawback bucket" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="own">Own-deal rate</option>
                            <option value="as_tagged">Rate of the deal clawed back</option>
                          </select>
                          <p className="text-[0.625rem] text-gray-500 mt-1">Own-deal rate reprices every clawback at the broker's own upfront split.</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1 block">Negative year end</label>
                          <select value={hurdleYearEndRule} onChange={e => setHurdleYearEndRule(e.target.value as HurdleYearEndRule)} aria-label="Hurdle year end rule" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="forgive">Forgive the shortfall</option>
                            <option value="carry">Carry into next year</option>
                            <option value="clawback">Owed back by the broker</option>
                          </select>
                          <p className="text-[0.625rem] text-gray-500 mt-1">What happens when the financial year closes below zero.</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1 block">Financial year starts</label>
                          <select value={hurdleFiscalYearStartMonth} onChange={e => setHurdleFiscalYearStartMonth(Number(e.target.value))} aria-label="Hurdle financial year start month" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((name, i) => (
                              <option key={name} value={i + 1}>{name}</option>
                            ))}
                          </select>
                          <p className="text-[0.625rem] text-gray-500 mt-1">The month the hurdle year resets. Monthly deficits net inside this year.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="h-px bg-gray-700 my-2" />
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Salary History</h4>
                  <div className="space-y-2">
                    {rawConfigHistory.filter(c => c.baseSalaryCents != null).length === 0 && (
                      <p className="text-xs text-gray-500">No salary history recorded yet.</p>
                    )}
                    {rawConfigHistory.filter(c => c.baseSalaryCents != null).map((c, i) => (
                      <div key={c.id} className={`flex items-center justify-between p-2.5 rounded-lg bg-vlt-dark border ${c.effectiveTo === null ? 'border-blue-500/30' : 'border-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${c.effectiveTo === null ? 'bg-blue-400' : 'bg-gray-500'}`} />
                          <span className={`text-sm font-semibold ${c.effectiveTo === null ? 'text-white' : 'text-gray-400'}`}>
                            {formatCurrencyWithCents(c.baseSalaryCents!)}/mo
                          </span>
                        </div>
                        <span className={`text-xs font-medium ${c.effectiveTo === null ? 'text-blue-400' : 'text-gray-500'}`}>
                          {formatDateShort(c.effectiveFrom)} — {c.effectiveTo ? formatDateShort(c.effectiveTo) : 'Current'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Broker Assistants — commission-sharing allocations.
              Admin can allocate a % of the broker's earned share to each
              linked assistant. Versioned via commission_config; changes
              create a new config doc and preserve historical splits.
              PR 1A-v2: hidden entirely when zero assistants linked to this
              broker AND zero existing allocations — keeps the page clean
              for tenants who don't use assistants. */}
          {(Object.keys(assistantSplits).length > 0 || availableAssistants.length > 0) && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <details open>
              <summary className="p-6 border-b border-gray-700 cursor-pointer list-none hover:bg-gray-700/20 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Broker Assistants</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Each assistant receives their sharePct of the broker's earned share per loan. Sum must be ≤ 100%.
                  </p>
                </div>
                {(() => {
                  const sum = Object.values(assistantSplits).reduce((s, v) => s + (v.sharePct || 0), 0);
                  const pillColor =
                    sum === 0 ? 'bg-gray-700 text-gray-400 border-gray-600'
                      : sum > 100 ? 'bg-red-500/20 text-red-400 border-red-500/40'
                      : sum === 100 ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                      : 'bg-blue-500/20 text-blue-400 border-blue-500/40';
                  return (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${pillColor}`}>
                      Allocated {sum}% {sum === 100 && '(broker keeps 0%)'}
                    </span>
                  );
                })()}
              </div>
              </summary>
            <div className="p-6 space-y-3">
              {Object.keys(assistantSplits).length === 0 && (
                <p className="text-sm text-gray-500 italic">
                  No assistants allocated. Click "Add Assistant" to share commission with a linked user.
                </p>
              )}
              {Object.entries(assistantSplits).map(([userId, split]) => (
                <div key={userId} className="flex items-start gap-3 p-3 rounded-lg bg-gray-900/60 border border-gray-700">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 flex-shrink-0">
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{split.displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{userId}</p>
                    <RecipientSplitControls
                      displayName={split.displayName}
                      upfrontPct={split.upfrontSharePct ?? split.sharePct}
                      trailPct={split.trailSharePct ?? split.sharePct}
                      onRatesChange={({ upfrontPct, trailPct }) => {
                        setAssistantSplits((prev) => ({
                          ...prev,
                          [userId]: {
                            ...prev[userId],
                            // sharePct stays the single-rate value so a legacy
                            // config that never touches the per-leg fields is
                            // written back exactly as it was read.
                            sharePct: upfrontPct === trailPct ? upfrontPct : prev[userId].sharePct,
                            ...(upfrontPct === trailPct
                              ? {}
                              : { upfrontSharePct: upfrontPct, trailSharePct: trailPct }),
                          },
                        }));
                      }}
                      options={split}
                      onOptionsChange={(next) => {
                        setAssistantSplits((prev) => {
                          const { base, reducesBrokerShare, appliesTo, byIntroduction, ...rest } = prev[userId];
                          return { ...prev, [userId]: { ...rest, ...next } };
                        });
                      }}
                      defaultAppliesTo="every_loan"
                      brokerSharePct={brokerIntroPct}
                      assignedOnlyLabel="Only their loans"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAssistantSplits((prev) => {
                        const next = { ...prev };
                        delete next[userId];
                        return next;
                      });
                    }}
                    className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                    title="Remove assistant"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Add Assistant — dropdown of eligible users not already allocated */}
              {(() => {
                const allocatedIds = new Set(Object.keys(assistantSplits));
                const candidates = availableAssistants.filter((a) => !allocatedIds.has(a.id));
                if (candidates.length === 0) {
                  return (
                    <p className="text-xs text-gray-500 italic pt-2">
                      {availableAssistants.length === 0
                        ? 'No linked broker-assistant users for this broker. Invite one first via Team Management.'
                        : 'All linked assistants are allocated.'}
                    </p>
                  );
                }
                if (!showAddAssistantPicker) {
                  return (
                    <button
                      type="button"
                      onClick={() => setShowAddAssistantPicker(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-700 border border-gray-600 text-gray-300 hover:text-white hover:bg-gray-600 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Assistant
                    </button>
                  );
                }
                return (
                  <div className="flex items-center gap-2 pt-2">
                    <select
                      autoFocus
                      onChange={(e) => {
                        const userId = e.target.value;
                        if (!userId) return;
                        const user = candidates.find((c) => c.id === userId);
                        if (!user) return;
                        setAssistantSplits((prev) => ({
                          ...prev,
                          [userId]: { sharePct: 10, displayName: user.displayName },
                        }));
                        setShowAddAssistantPicker(false);
                      }}
                      defaultValue=""
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="" disabled>Select an assistant…</option>
                      {candidates.map((c) => (
                        <option key={c.id} value={c.id}>{c.displayName} {c.email && `· ${c.email}`}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowAddAssistantPicker(false)}
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })()}

              <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <p className="text-xs text-purple-400/80">
                  <strong>How splits work:</strong> each assistant's share comes out of the broker's earned
                  portion (post company/broker intro split). If the broker share is $60 and an assistant's
                  sharePct is 20%, the assistant earns $12; the broker nets $48.
                </p>
              </div>
            </div>
            </details>
          </div>
          )}

          {/* Credit Analysts — same arithmetic as Broker Assistants. Share
              comes out of the broker's earned portion. The 100% cap is SHARED
              with assistants (combined sum ≤ 100%). Records may have a login
              (credit_analyst user) or be operational-only (created via
              Team Management → Operational Roles).
              PR 1A-v2: hidden entirely when zero analysts available AND zero
              existing allocations — keeps the page clean for tenants who
              don't use credit analysts. Per-allocation paymentMode + target
              configured inline (mirrors how brokers configure their own % +
              targets on this same page). */}
          {(Object.keys(creditAnalystSplits).length > 0 || availableAnalysts.length > 0) && (() => {
            // PR 1A-v3 (2026-05-13): two independent budgets — upfront and
            // trail — with assistant.sharePct counting in BOTH because
            // assistant share applies uniformly across payment types.
            const aSum = Object.values(assistantSplits).reduce(
              (s, v) => s + (v.sharePct || 0),
              0,
            );
            const cUpfrontSum = Object.values(creditAnalystSplits).reduce(
              (s, v) => s + (v.upfrontSharePct || 0),
              0,
            );
            const cTrailSum = Object.values(creditAnalystSplits).reduce(
              (s, v) => s + (v.trailSharePct || 0),
              0,
            );
            const upfrontBudget = aSum + cUpfrontSum;
            const trailBudget = aSum + cTrailSum;
            const budgetPill = (used: number): string =>
              used === 0
                ? 'bg-gray-700 text-gray-400'
                : used > 100
                  ? 'bg-red-500/20 text-red-400'
                  : used === 100
                    ? 'bg-amber-500/20 text-amber-300'
                    : 'bg-blue-500/20 text-blue-300';
            const allocated = new Set(Object.keys(creditAnalystSplits));
            const eligible = availableAnalysts.filter((a) => !allocated.has(a.id));
            return (
              <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
                <details open>
                  <summary className="p-6 border-b border-gray-700 flex items-start justify-between gap-4 cursor-pointer list-none hover:bg-gray-700/20 transition-colors">
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-white">Credit Analysts</h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Separate upfront and trail share per analyst. Performance targets for analysts are configured on the Performance Targets tab — pick "Credit Analyst" as the subject when creating a target.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 items-end shrink-0">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${budgetPill(upfrontBudget)}`}>
                      Upfront {upfrontBudget}%
                    </span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${budgetPill(trailBudget)}`}>
                      Trail {trailBudget}%
                    </span>
                  </div>
                  </summary>
                <div className="p-6 space-y-3">
                  {Object.keys(creditAnalystSplits).length === 0 && (
                    <p className="text-sm text-gray-500 italic">
                      No credit analysts allocated. Click "Add Credit Analyst" below to share commission. Operational-only analysts (no login) can be added via Team Management → Invite User (leave email blank).
                    </p>
                  )}
                  {Object.entries(creditAnalystSplits).map(([analystId, split]) => {
                    // PR 1A-v5 (2026-05-14): surface the analyst's other-broker
                    // allocations from the overview rollup so admins can see at
                    // a glance that this analyst is linked across multiple
                    // brokers. Performance-target progress now combines across
                    // every broker she's allocated on, so this info matters.
                    const rollupEntry = overviewAnalystRollup.find((r) => r.analystId === analystId);
                    const otherBrokers = rollupEntry
                      ? rollupEntry.brokers.filter((b) => b.brokerDocId !== brokerDocId)
                      : [];
                    return (
                      <div
                        key={analystId}
                        className="p-4 rounded-lg bg-vlt-dark border border-gray-700"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-white">{split.displayName}</span>
                              {!availableAnalysts.find((a) => a.id === analystId)?.hasLogin && (
                                <span className="px-1.5 py-0.5 text-[0.625rem] font-bold rounded bg-gray-700 text-gray-400 border border-gray-600">
                                  NO LOGIN
                                </span>
                              )}
                              {otherBrokers.length > 0 && (
                                <span
                                  className="px-1.5 py-0.5 text-[0.625rem] font-bold rounded bg-emerald-700/30 text-emerald-200 border border-emerald-500/40"
                                  title={
                                    `Also allocated on: ${
                                      otherBrokers
                                        .map((b) => `${b.brokerName} (${b.upfrontSharePct}% upfront / ${b.trailSharePct}% trail)`)
                                        .join(', ')
                                    }. Performance targets combine progress across all allocated brokers.`
                                  }
                                >
                                  ALSO ON: {otherBrokers.map((b) => b.brokerName).join(' + ')}
                                </span>
                              )}
                          <RecipientSplitControls
                            displayName={split.displayName}
                            upfrontPct={split.upfrontSharePct}
                            trailPct={split.trailSharePct}
                            onRatesChange={({ upfrontPct, trailPct }) =>
                              setCreditAnalystSplits((prev) => ({
                                ...prev,
                                [analystId]: {
                                  ...prev[analystId],
                                  upfrontSharePct: upfrontPct,
                                  trailSharePct: trailPct,
                                },
                              }))
                            }
                            options={split}
                            onOptionsChange={(next) =>
                              setCreditAnalystSplits((prev) => {
                                const { base, reducesBrokerShare, appliesTo, byIntroduction, ...rest } = prev[analystId];
                                return { ...prev, [analystId]: { ...rest, ...next } };
                              })
                            }
                            defaultAppliesTo="assigned_only"
                            brokerSharePct={brokerIntroPct}
                            assignedOnlyLabel="Only tagged loans"
                          />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setCreditAnalystSplits((prev) => {
                                const next = { ...prev };
                                delete next[analystId];
                                return next;
                              })
                            }
                            className="p-1.5 rounded-md text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                            title="Remove credit analyst"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {showAddAnalystPicker ? (
                    eligible.length > 0 ? (
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) return;
                          const a = availableAnalysts.find((x) => x.id === id);
                          if (!a) return;
                          // PR 1A-v5 (2026-05-14): when adding an analyst who's
                          // already allocated on other brokers, pre-fill the
                          // upfront/trail pct from one of their existing
                          // allocations rather than the generic 10/10 default.
                          // Admin can still edit before save. Reduces "no way
                          // to apply the same allocation to multiple brokers"
                          // friction.
                          const rollupEntry = overviewAnalystRollup.find((r) => r.analystId === id);
                          const sample = rollupEntry?.brokers[0];
                          setCreditAnalystSplits((prev) => ({
                            ...prev,
                            [id]: {
                              upfrontSharePct: sample?.upfrontSharePct ?? 10,
                              trailSharePct: sample?.trailSharePct ?? 10,
                              displayName: a.displayName,
                            },
                          }));
                          setShowAddAnalystPicker(false);
                        }}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="" disabled>
                          Select a credit analyst…
                        </option>
                        {eligible.map((a) => {
                          // Surface other-broker allocations in the option label
                          // so admins choosing whom to add can see "Hanah is
                          // already on Andrew Timms" without leaving the dropdown.
                          const rollupEntry = overviewAnalystRollup.find((r) => r.analystId === a.id);
                          const otherBrokers = rollupEntry?.brokers ?? [];
                          const otherLabel = otherBrokers.length > 0
                            ? ` — also on: ${otherBrokers.map((b) => b.brokerName).join(', ')}`
                            : '';
                          return (
                            <option key={a.id} value={a.id}>
                              {a.displayName}{a.hasLogin ? '' : ' (no login)'}{otherLabel}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <p className="text-xs text-gray-500 italic">
                        No more analysts available. Add new ones via Team Management → Invite User (leave email blank for operational-only records).
                      </p>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddAnalystPicker(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Credit Analyst
                    </button>
                  )}
                </div>
                </details>

                <div className="px-6 pb-6">
                  <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                    <p className="text-xs text-blue-400/80">
                      <strong>How splits work:</strong> analyst's share comes out of the broker's earned portion. Two independent budgets: combined upfront and combined trail each cap at 100% (assistant share counts in both because it applies uniformly).
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}


          {/* Unified per-person payment type visibility. Tri-state per leg:
              Hidden (no rows of that leg), Payout only (rows without tenant
              gross), Full (current default). Selections apply on Save.
              Broader access controls remain in Team Management. */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">Payment Type Visibility</h3>
              <p className="text-sm text-gray-400 mt-1">
                Control what each team member sees on their commissions page. Their own payout and effective rate always remain visible.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Team Member</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Upfront Commissions</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Trail Commissions</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionVisibilityRecipients.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                        No unique login account could be resolved for this broker or its allocated recipients.
                      </td>
                    </tr>
                  )}
                  {commissionVisibilityRecipients.map((recipient) => {
                    const roleLabel =
                      recipient.role === 'broker_assistant'
                        ? 'Broker Assistant'
                        : recipient.role === 'credit_analyst'
                          ? 'Credit Analyst'
                          : 'Broker';
                    return (
                      <tr key={recipient.id} className="border-b border-gray-800 hover:bg-gray-900/50">
                        <td className="px-6 py-4">
                          <p className="text-white font-semibold">{recipient.displayName}</p>
                          {recipient.email && <p className="text-xs text-gray-500 mt-0.5">{recipient.email}</p>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">{roleLabel}</td>
                        {(['upfront', 'trail'] as LegKey[]).map((leg) => {
                          const current = legStateFor(recipient, leg);
                          const options: Array<{
                            value: LegVisibilityState;
                            label: string;
                            selectedClass: string;
                          }> = [
                            {
                              value: 'hidden',
                              label: 'Hidden',
                              selectedClass:
                                'border-rose-500/40 bg-rose-500/15 text-rose-300',
                            },
                            {
                              value: 'payout_only',
                              label: 'Payout only',
                              selectedClass:
                                'border-amber-500/40 bg-amber-500/15 text-amber-300',
                            },
                            {
                              value: 'full',
                              label: 'Full',
                              selectedClass:
                                'border-green-500/30 bg-green-500/15 text-green-300',
                            },
                          ];
                          return (
                            <td key={leg} className="px-6 py-4 text-center">
                              <div
                                role="radiogroup"
                                aria-label={`${recipient.displayName} ${leg} commission visibility`}
                                className="inline-flex overflow-hidden rounded-lg border border-gray-700 bg-gray-900"
                              >
                                {options.map((option) => {
                                  const selected = current === option.value;
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      role="radio"
                                      aria-checked={selected}
                                      onClick={() => selectLegState(recipient, leg, option.value)}
                                      className={`border px-3 py-1.5 text-xs font-bold transition-colors ${
                                        selected
                                          ? option.selectedClass
                                          : 'border-transparent text-gray-500 hover:text-gray-300'
                                      }`}
                                    >
                                      {option.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 bg-blue-500/5">
              <p className="text-xs text-blue-400/80">
                <strong>Hidden</strong> removes that payment type from all of this person's views (existing behaviour). <strong>Payout only</strong> removes the tenant gross column on the commissions page only; no other page is affected. Selections apply when you press Save.
              </p>
            </div>
          </div>

          {/* Commission Forecast Impact */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between"><div><h3 className="text-lg font-bold text-white">Commission Forecast Impact</h3><p className="text-sm text-gray-400 mt-1">See how changing split percentages affects broker income</p></div><span className="inline-flex items-center px-2 py-0.5 rounded text-[0.625rem] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-400">CALCULATOR</span></div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-vlt-dark rounded-lg border border-gray-700 p-5"><p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">Current Configuration</p><div className="space-y-3"><div className="flex justify-between"><span className="text-sm text-gray-400">Broker Intro ({brokerIntroPct}%)</span><span className="text-sm font-semibold text-white">{loanTags.filter(l => l.introductionType === 'broker').length} loans</span></div><div className="flex justify-between"><span className="text-sm text-gray-400">Company Intro ({companyIntroPct}%)</span><span className="text-sm font-semibold text-white">{loanTags.filter(l => l.introductionType === 'company').length} loans</span></div><div className="flex justify-between"><span className="text-sm text-gray-400">Untagged (default Co. {companyIntroPct}%)</span><span className="text-sm font-semibold text-amber-400">{untaggedCount} loans</span></div></div></div>
                <div className="bg-vlt-dark rounded-lg border border-blue-500/30 p-5 ring-1 ring-blue-500/20"><p className="text-xs text-blue-400 uppercase tracking-wider font-semibold mb-3">What-If Scenario</p><div className="space-y-3"><div><label className="text-xs text-gray-500 mb-1 block">Broker Intro %</label><input type="number" value={whatIfBrokerPct} onChange={e => setWhatIfBrokerPct(Number(e.target.value))} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500" /></div><div><label className="text-xs text-gray-500 mb-1 block">Company Intro %</label><input type="number" value={whatIfCompanyPct} onChange={e => setWhatIfCompanyPct(Number(e.target.value))} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500" /></div></div></div>
                <div className="bg-vlt-dark rounded-lg border border-gray-700 p-5 flex flex-col justify-center"><p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-4">Impact Summary</p><div className="text-center space-y-2"><div className="text-sm text-gray-400">Adjust percentages to see projected impact</div></div><div className="mt-4 p-2 rounded bg-blue-500/10 border border-blue-500/20"><p className="text-[0.6875rem] text-blue-400/80 text-center">Based on tagged loan data</p></div></div>
              </div>
            </div>
          </div>

          {/* Configuration History */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700"><h3 className="text-lg font-bold text-white">Configuration History</h3><p className="text-sm text-gray-400 mt-1">Point-in-time audit trail of all setting changes</p></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700"><tr>{['Effective From', 'Effective To'].map(h => <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}{['Broker Intro %', 'Company Intro %', 'Base Salary'].map(h => <th key={h} className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}{['Visibility', 'Changed By'].map(h => <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead>
                <tbody>
                  {configHistory.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">No configuration history yet. Save settings to create the first entry.</td></tr>
                  )}
                  {configHistory.map((c, i) => (
                    <tr key={i} className={`border-b border-gray-800 ${c.effectiveTo === null ? 'bg-blue-500/5' : 'hover:bg-vlt-dark'} transition-colors`}>
                      <td className="py-4 px-6 text-white font-medium">{c.effectiveFrom}</td>
                      <td className="py-4 px-6">{c.effectiveTo === null ? <span className="px-2 py-0.5 text-[0.625rem] font-bold rounded bg-green-500/20 text-green-400 border border-green-500/30">CURRENT</span> : <span className="text-gray-400 text-sm">{c.effectiveTo}</span>}</td>
                      <td className="py-4 px-6 text-right text-blue-400 font-semibold">{c.brokerIntroPct}%</td>
                      <td className="py-4 px-6 text-right text-purple-400 font-semibold">{c.companyIntroPct}%</td>
                      <td className="py-4 px-6 text-right text-green-400 font-semibold">{formatCurrency(c.baseSalaryCents)}</td>
                      <td className="py-4 px-6"><div className="flex gap-1">{c.visibleUpfront && <span className={`px-1.5 py-0.5 text-[0.625rem] rounded ${c.effectiveTo === null ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-500/10 text-blue-400/60'}`}>Upfront</span>}{c.visibleTrail && <span className={`px-1.5 py-0.5 text-[0.625rem] rounded ${c.effectiveTo === null ? 'bg-green-500/20 text-green-300' : 'bg-green-500/10 text-green-400/60'}`}>Trail</span>}</div></td>
                      <td className="py-4 px-6 text-gray-400 text-sm">{c.changedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Manual Adjustments — now integrated into monthly view below.
             Kept only the loading/error indicators. */}
          {adjustmentsError && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4 text-rose-400 text-sm">
              Failed to load manual adjustments: {adjustmentsError}
            </div>
          )}

          {/* Loan Commission Tags Tree */}
          <div id="loan-tags" className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            <div className="p-6 border-b border-gray-700 bg-gray-800 shrink-0">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h3 className="text-lg font-bold text-white">Loan Commission Tags</h3><p className="text-sm text-gray-400 mt-1">Every commission payment, grouped by financial year and month. Tag each loan with how it was introduced; the tag applies to every payment on that loan.{treeIsFiltered && <span className="ml-2 px-1.5 py-0.5 text-[0.625rem] font-semibold rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 uppercase tracking-wider">Filtered: subtotals cover matching payments only</span>}</p></div>
                {rawConfigHistory.length === 0 && (
                  /* No saved commission_config for this broker. getConfigForMonth
                     falls back to the in-flight form values (default 60/40), so every
                     Broker Share below is an ESTIMATE at a rate nobody agreed. The
                     all-brokers overview correctly shows "Not configured" for these
                     brokers; without this notice the two views silently disagree. */
                  <div className="w-full flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-200">
                      <strong>No commission split configured for this broker.</strong>{' '}
                      Broker Share shows "Not configured" below because no split has been saved.
                      Gross commission is still shown. Set the percentages above and save to value this broker.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <button disabled={selectedLoanIds.size === 0 || isReadOnly} title={isReadOnly ? 'Read-only in view-as mode' : undefined} onClick={() => handleBulkTag('broker')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Tag className="w-3.5 h-3.5" />Broker ({selectedLoanIds.size})</button>
                  </div>
                  <button disabled={selectedLoanIds.size === 0 || isReadOnly} title={isReadOnly ? 'Read-only in view-as mode' : undefined} onClick={() => handleBulkTag('company')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Tag className="w-3.5 h-3.5" />Company ({selectedLoanIds.size})</button>
                  {/* PR 1A-v4 (2026-05-14): bulk-tag credit analyst. Rendered
                      only when at least one credit analyst is allocated on
                      this broker. "Clear" option un-tags the selected loans
                      so they stop paying any analyst. */}
                  {Object.keys(creditAnalystSplits).length > 0 && (
                    <select
                      disabled={selectedLoanIds.size === 0 || isReadOnly}
                      title={isReadOnly ? 'Read-only in view-as mode' : 'Tag selected loans to a credit analyst'}
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        if (v === '__clear__') {
                          handleBulkTagAnalyst(null);
                        } else {
                          handleBulkTagAnalyst(v);
                        }
                        // Reset the select back to placeholder.
                        e.currentTarget.value = '';
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <option value="" disabled>Analyst ({selectedLoanIds.size})…</option>
                      {Object.entries(creditAnalystSplits).map(([id, split]) => (
                        <option key={id} value={id}>
                          {split.displayName}
                        </option>
                      ))}
                      <option value="__clear__">— Untag analyst —</option>
                    </select>
                  )}
                  <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className="bg-gray-700 border border-gray-600 text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="all">All payments ({loanTags.length})</option>
                    <option value="untagged">Untagged ({loanTags.filter(l => l.isUntagged).length})</option>
                    <option value="broker">Broker Introduction ({loanTags.filter(l => l.introductionType === 'broker').length})</option>
                    <option value="company">Company Introduction ({loanTags.filter(l => l.introductionType === 'company').length})</option>
                    {threeTierActive && <option value="self_sourced">Self-Sourced ({loanTags.filter(l => l.introductionType === 'self_sourced').length})</option>}
                    {/* PR 1A-v4.1: per-analyst filter. Only rendered when at
                        least one analyst is allocated on this broker so the
                        dropdown stays compact for tenants without analysts. */}
                    {Object.keys(creditAnalystSplits).length > 0 && (
                      <optgroup label="Credit Analyst">
                        <option value="analyst-unassigned">— No analyst tag — ({loanTags.filter(l => !l.creditAnalystId).length})</option>
                        {Object.entries(creditAnalystSplits).map(([id, split]) => (
                          <option key={`analyst:${id}`} value={`analyst:${id}`}>
                            {split.displayName} ({loanTags.filter(l => l.creditAnalystId === id).length})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <div className="relative"><Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" /><input type="text" value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search loan ID or customer..." className="bg-gray-700 border border-gray-600 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 w-56" /></div>
                  <button onClick={expandAll} className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors">Expand All</button>
                  <button onClick={collapseAll} className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors">Collapse All</button>
                </div>
              </div>
            </div>
            {tagsLoading && <LoadingState message="Loading loan tags..." />}
            {!tagsLoading && (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full">
                  <thead className="bg-gray-900 border-b border-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-4 text-left w-10"><input type="checkbox" className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" title="Select all" onChange={() => { setSelectedLoanIds(prev => { const allTaggable = yearGroups.flatMap(yg => yg.months.flatMap(mg => mg.loans)).filter(isTaggableRow); if (allTaggable.every(l => prev.has(l.loanId))) return new Set(); const next = new Set(prev); allTaggable.forEach(l => next.add(l.loanId)); return next; }); }} checked={(() => { const allTaggable = yearGroups.flatMap(yg => yg.months.flatMap(mg => mg.loans)).filter(isTaggableRow); return allTaggable.length > 0 && allTaggable.every(l => selectedLoanIds.has(l.loanId)); })()} /></th>
                      {([['loanId', 'Period / Loan ID', 'left'], ['customer', 'Customer', 'left'], ['lender', 'Lender', 'left'], ['loanValue', 'Loan Amount', 'right'], ['type', 'Type', 'left']] as const).map(([col, label, align]) => (
                        <th key={col} className={`px-4 py-4 text-${align} text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors`} onClick={() => setLoanSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                          {label}{loanSort.col === col && <span className="ml-1">{loanSort.dir === 'asc' ? '↑' : '↓'}</span>}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors" onClick={() => setLoanSort(prev => ({ col: 'commission', dir: prev.col === 'commission' && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                        <span title="Commission paid by the aggregator, before the broker split">Gross Commission</span>{loanSort.col === 'commission' && <span className="ml-1">{loanSort.dir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      <th className="px-4 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Introduction</th>
                      {/* PR 1A-v4 (2026-05-14): Credit Analyst column. Renders the
                          per-loan analyst tag; admin can change it via the dropdown
                          inline. Hidden via colSpan adjustment on aggregator rows
                          when no analysts are allocated on this broker — see body. */}
                      {Object.keys(creditAnalystSplits).length > 0 && (
                        <th className="px-4 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Analyst</th>
                      )}
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors" onClick={() => setLoanSort(prev => ({ col: 'share', dir: prev.col === 'share' && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                        {hasAllocations ? <span title="Broker share after assistant and analyst allocations. Same basis as the reconciliation table above.">Broker Net</span> : 'Broker Share'}{loanSort.col === 'share' && <span className="ml-1">{loanSort.dir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearGroups.length === 0 && (
                      <tr><td colSpan={Object.keys(creditAnalystSplits).length > 0 ? 11 : 10} className="px-4 py-8 text-center text-gray-500">No loan tags found. Tags will appear as loans are tagged.</td></tr>
                    )}
                    {yearGroups.map(yg => (
                      <React.Fragment key={yg.year}>
                        <tr className="bg-gray-700/30 border-b border-gray-600 cursor-pointer" onClick={() => toggleYear(yg.year)}>
                          <td className="px-4 py-3"><input type="checkbox" checked={yg.months.flatMap(m => m.loans).filter(isTaggableRow).every(l => selectedLoanIds.has(l.loanId))} onChange={(e) => { e.stopPropagation(); setSelectedLoanIds(prev => { const next = new Set(prev); const taggable = yg.months.flatMap(m => m.loans).filter(isTaggableRow); if (taggable.every(l => next.has(l.loanId))) { taggable.forEach(l => next.delete(l.loanId)); } else { taggable.forEach(l => next.add(l.loanId)); } return next; }); }} onClick={e => e.stopPropagation()} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" /></td>
                          <td className="px-4 py-3 text-white font-bold text-base" colSpan={2}><span className="inline-flex items-center gap-2"><ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedYears.has(yg.year) ? 'rotate-90' : ''}`} />{yg.label}<span className="text-xs font-normal text-gray-500">{yg.rangeLabel}</span></span></td>
                          <td className="px-4 py-3 text-gray-500 text-sm">{yg.months.length} month{yg.months.length === 1 ? '' : 's'}</td><td className="px-4 py-3" /><td className="px-4 py-3" />
                          <td className="px-4 py-3 text-right text-green-400 font-bold">{formatCurrencyWithCents(yg.totalCommsCents)}</td><td className="px-4 py-3" />
                          {/* PR 1A-v4: empty placeholder when Analyst column is rendered. */}
                          {Object.keys(creditAnalystSplits).length > 0 && <td className="px-4 py-3" />}
                          <td className="px-4 py-3 text-right text-green-400 font-bold">{notConfigured ? <span className="text-gray-500 italic font-normal text-sm">Not configured</span> : formatCurrencyWithCents(yg.totalBrokerShareCents)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{yg.paymentCount} payments · {yg.loanCount} loans</td>
                        </tr>
                        {expandedYears.has(yg.year) && yg.months.map(mg => (
                          <React.Fragment key={mg.month}>
                            <tr className={`bg-gray-700/40 border-b border-gray-600 border-t border-t-gray-600 cursor-pointer hover:bg-gray-700/60 transition-colors ${mg.month === currentMonth ? 'bg-blue-900/30' : ''}`} onClick={() => toggleMonth(mg.month)}>
                              <td className="px-4 py-3 pl-8"><input type="checkbox" checked={mg.loans.filter(isTaggableRow).every(l => selectedLoanIds.has(l.loanId))} onChange={(e) => { e.stopPropagation(); setSelectedLoanIds(prev => { const next = new Set(prev); const taggable = mg.loans.filter(isTaggableRow); if (taggable.every(l => next.has(l.loanId))) { taggable.forEach(l => next.delete(l.loanId)); } else { taggable.forEach(l => next.add(l.loanId)); } return next; }); }} onClick={e => e.stopPropagation()} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" /></td>
                              <td className="px-4 py-3 text-cyan-400 font-semibold" colSpan={2}><span className="inline-flex items-center gap-2"><ChevronRight className={`w-3 h-3 transition-transform duration-200 ${expandedMonths.has(mg.month) ? 'rotate-90' : ''}`} />{MONTH_NAMES[parseInt(mg.month.split('-')[1], 10) - 1]}{mg.month === currentMonth && <span className="px-1.5 py-0.5 text-[0.625rem] font-bold rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">CURRENT</span>}</span></td>
                              <td className="px-4 py-3 text-gray-500 text-sm whitespace-nowrap">{mg.paymentCount} payments · {mg.loanCount} loans</td><td className="px-4 py-3" /><td className="px-4 py-3" />
                              <td className="px-4 py-3 text-right text-green-400 font-semibold">{formatCurrencyWithCents(mg.totalCommsCents)}</td><td className="px-4 py-3" />
                              {/* PR 1A-v4: empty placeholder when Analyst column is rendered. */}
                              {Object.keys(creditAnalystSplits).length > 0 && <td className="px-4 py-3" />}
                              <td className="px-4 py-3 text-right text-green-400 font-semibold">{notConfigured ? <span className="text-gray-500 italic font-normal text-sm">Not configured</span> : formatCurrencyWithCents(mg.totalBrokerShareCents)}</td>
                              <td className="px-4 py-3"><div className="flex items-center gap-1.5 flex-nowrap">{mg.referrerDeductionCount > 0 && <span className="whitespace-nowrap px-1.5 py-0.5 text-[0.625rem] rounded bg-violet-500/20 text-violet-400 font-semibold" title="Third-party referrer fees on this month's loans. Paid by the company, not deducted from the broker.">{mg.referrerDeductionCount} referrer</span>}{mg.untaggedCount > 0 && <span className="whitespace-nowrap px-1.5 py-0.5 text-[0.625rem] rounded bg-amber-500/20 text-amber-400 font-semibold">{mg.untaggedCount} untagged</span>}{mg.arrearsCount > 0 && <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">ARREARS</span>}{mg.dischargedCount > 0 && <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30">{mg.dischargedCount} discharged</span>}</div></td>
                            </tr>
                            {expandedMonths.has(mg.month) && (() => {
                              const recon = reconByMonth.get(mg.month);
                              if (!recon || recon.baseSalaryCents <= 0) return null;
                              const amtOnTop = recon.amtOnTopCents;
                              const carryOver = recon.carryOverCents;
                              return (
                                <tr className="border-b border-gray-700/50">
                                  <td colSpan={Object.keys(creditAnalystSplits).length > 0 ? 11 : 10} className="px-4 py-3 pl-8">
                                    <div className="flex items-center gap-2">
                                      {(() => {
                                        const assistantsThisMonth = monthlyAssistantPayouts.get(mg.month) || 0;
                                        return (
                                      <div className={`grid ${assistantsThisMonth > 0 ? 'grid-cols-5' : 'grid-cols-4'} gap-px rounded-lg overflow-hidden border border-gray-600/50 text-sm w-fit`}>
                                        <div className="bg-gray-700/60 px-5 py-2.5 text-center">
                                          <div className="text-gray-400 font-medium text-xs mb-1">
                                            Broker {assistantsThisMonth > 0 ? 'Net' : 'Share'}
                                          </div>
                                          <div className="text-white font-bold">{formatCurrencyWithCents(recon.commsCents)}</div>
                                        </div>
                                        {assistantsThisMonth > 0 && (
                                          <div className="bg-purple-500/10 px-5 py-2.5 text-center border-l border-purple-500/20">
                                            <div className="text-purple-300 font-medium text-xs mb-1" title="Commission allocated out of the broker's share to broker assistants and credit analysts.">Recipient Payouts</div>
                                            <div className="text-purple-400 font-bold">
                                              -{formatCurrencyWithCents(assistantsThisMonth)}
                                            </div>
                                          </div>
                                        )}
                                        <div className="bg-gray-700/60 px-5 py-2.5 text-center">
                                          <div className="text-gray-400 font-medium text-xs mb-1">Base Salary</div>
                                          <div className="text-gray-300 font-bold">{formatCurrencyWithCents(recon.baseSalaryCents)}</div>
                                        </div>
                                        <div className={`px-5 py-2.5 text-center ${amtOnTop >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                          <div className="text-gray-400 font-medium text-xs mb-1">Amt On Top of Base</div>
                                          <div className={`font-bold ${amtOnTop >= 0 ? 'text-green-400' : 'text-red-400'}`}>{amtOnTop < 0 ? `-${formatCurrencyWithCents(Math.abs(amtOnTop))}` : formatCurrencyWithCents(amtOnTop)}</div>
                                        </div>
                                        <div className={`px-5 py-2.5 text-center ${carryOver < 0 ? 'bg-red-500/10' : 'bg-gray-700/60'}`}>
                                          <div className="text-gray-400 font-medium text-xs mb-1">Carry Over</div>
                                          <div className={`font-bold ${carryOver < 0 ? 'text-red-400' : 'text-gray-500'}`}>{carryOver < 0 ? `-${formatCurrencyWithCents(Math.abs(carryOver))}` : '$0.00'}</div>
                                        </div>
                                      </div>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })()}
                            {expandedMonths.has(mg.month) && mg.loans.map(loan => (
                              <tr key={paymentRowKey(loan)} className={`border-b border-gray-800/50 transition-colors hover:bg-vlt-dark/50 ${loan.isManualAdjustment ? 'bg-indigo-500/5' : loan.isUntagged ? 'bg-amber-500/5' : ''}`}>
                                <td className="px-4 py-3 pl-14">{!isTaggableRow(loan) ? <span className="w-4 h-4 inline-block" /> : <input type="checkbox" checked={selectedLoanIds.has(loan.loanId)} onChange={() => toggleLoanSelection(loan.loanId)} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" />}</td>
                                <td className="px-4 py-3 text-blue-400 font-mono text-sm">{loan.loanId}</td>
                                <td className="px-4 py-3 text-white text-sm">{loan.customerName}</td>
                                <td className="px-4 py-3 text-gray-400 text-sm">{loan.lender}</td>
                                {/* Settled amount on the upfront leg, current balance on trail.
                                    A blank source cell stays null and renders as an em dash -
                                    NEVER $0, which would read as a real zero-value loan. */}
                                <td className="px-4 py-3 text-right text-gray-300 text-sm tabular-nums" title={loan.loanValueCents == null ? undefined : loan.paymentType === 'upfront' ? 'Settled loan amount for this commission event' : 'Loan balance at this period, or the settled amount where the lender did not report a balance'}>{loan.loanValueCents == null ? <span className="text-gray-600">—</span> : formatCurrency(loan.loanValueCents)}</td>
                                <td className="px-4 py-3"><PaymentTypeBadge type={loan.paymentType} /></td>
                                <td className={`px-4 py-3 text-right font-semibold ${loan.commissionCents < 0 ? 'text-rose-400' : 'text-white'}`}>{formatCurrencyWithCents(loan.commissionCents)}</td>
                                <td className="px-4 py-3 text-center">
                                  {loan.isArrears || loan.isDischarged || loan.isReferrerDeduction ? (
                                    <span className={`px-2 py-1 text-xs rounded-md ${loan.isDischarged ? 'text-rose-400' : 'text-amber-400'}`}>—</span>
                                  ) : (() => {
                                    const displayPct = loan.lockedPct ?? (loan.introductionType === 'broker' ? brokerIntroPct : companyIntroPct);
                                    return (
                                    <div className="flex items-center justify-center gap-1.5">
                                      <select
                                        value={loan.introductionType ?? ''}
                                        onChange={e => {
                                          const val = e.target.value as 'broker' | 'company' | 'self_sourced';
                                          if (val) {
                                            // PR C — self_sourced uses the upfront pct from config for
                                            // lockedPct AND requires lockedTrailPct (backend rejects without it).
                                            const pctToLock =
                                              val === 'broker'
                                                ? brokerIntroPct
                                                : val === 'company'
                                                  ? companyIntroPct
                                                  : upfrontSelfSourcedPct;
                                            const trailPctToLock =
                                              val === 'self_sourced' ? trailSelfSourcedPct : undefined;
                                            if (loan.isManualAdjustment && loan.manualAdjustmentId) {
                                              handleUpdateAdjustmentIntro(loan.manualAdjustmentId, val, pctToLock);
                                            } else {
                                              handleTagLoan(loan.loanId, val, pctToLock, loan.customerName, loan.lender, trailPctToLock);
                                            }
                                          }
                                        }}
                                        className={`rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${loan.isUntagged ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' : loan.introductionType === 'broker' ? 'bg-blue-500/20 border border-blue-500/40 text-blue-300' : 'bg-purple-500/20 border border-purple-500/40 text-purple-300'}`}
                                      >
                                        {loan.isUntagged && <option value="" disabled>Select Introduction</option>}
                                        <option value="broker">Broker ({brokerIntroPct}%)</option>
                                        <option value="company">Company ({companyIntroPct}%)</option>
                                        {threeTierActive && <option value="self_sourced">Self-Sourced ({upfrontSelfSourcedPct}/{trailSelfSourcedPct}%)</option>}
                                      </select>
                                      {!loan.isUntagged && (
                                        editingPctLoanId === (loan.isManualAdjustment ? `manual_${loan.manualAdjustmentId}` : loan.loanId) ? (
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={editingPctValue}
                                            onChange={e => setEditingPctValue(e.target.value)}
                                            onKeyDown={e => {
                                              if (e.key === 'Enter') {
                                                const newPct = parseInt(editingPctValue, 10);
                                                if (newPct >= 0 && newPct <= 100 && loan.introductionType) {
                                                  if (loan.isManualAdjustment && loan.manualAdjustmentId) {
                                                    handleUpdateAdjustmentIntro(loan.manualAdjustmentId, loan.introductionType, newPct);
                                                  } else {
                                                    handleTagLoan(loan.loanId, loan.introductionType, newPct, loan.customerName, loan.lender);
                                                  }
                                                }
                                                setEditingPctLoanId(null);
                                              } else if (e.key === 'Escape') {
                                                setEditingPctLoanId(null);
                                              }
                                            }}
                                            onBlur={() => {
                                              const newPct = parseInt(editingPctValue, 10);
                                              if (newPct >= 0 && newPct <= 100 && loan.introductionType) {
                                                if (loan.isManualAdjustment && loan.manualAdjustmentId) {
                                                  handleUpdateAdjustmentIntro(loan.manualAdjustmentId, loan.introductionType, newPct);
                                                } else {
                                                  handleTagLoan(loan.loanId, loan.introductionType, newPct, loan.customerName, loan.lender);
                                                }
                                              }
                                              setEditingPctLoanId(null);
                                            }}
                                            autoFocus
                                            className="w-12 rounded-md px-1 py-0.5 text-xs text-center bg-gray-700 border border-gray-500 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                        ) : (
                                          <button
                                            onClick={() => {
                                              setEditingPctLoanId(loan.isManualAdjustment ? `manual_${loan.manualAdjustmentId}` : loan.loanId);
                                              setEditingPctValue(String(displayPct));
                                            }}
                                            className={`rounded-md px-1.5 py-0.5 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${loan.introductionType === 'broker' ? 'bg-green-500/20 border border-green-500/40 text-green-300' : 'bg-red-500/20 border border-red-500/40 text-red-300'}`}
                                            title="Click to edit percentage"
                                          >
                                            {displayPct}%
                                          </button>
                                        )
                                      )}
                                    </div>
                                    );
                                  })()}
                                </td>
                                {/* PR 1A-v4: Credit Analyst tag dropdown. Only
                                    rendered when this broker has at least one
                                    analyst allocated. "Unassigned" = no analyst
                                    gets paid on this loan (strict ownership). */}
                                {Object.keys(creditAnalystSplits).length > 0 && (
                                  <td className="px-4 py-3 text-center">
                                    {loan.isArrears || loan.isDischarged || loan.isReferrerDeduction || loan.isManualAdjustment ? (
                                      <span className="text-xs text-gray-500">
                                        {loan.creditAnalystId
                                          ? creditAnalystSplits[loan.creditAnalystId]?.displayName ?? '—'
                                          : '—'}
                                      </span>
                                    ) : (
                                      <select
                                        value={loan.creditAnalystId ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          handleTagAnalyst(loan.loanId, v === '' ? null : v, loan.customerName, loan.lender);
                                        }}
                                        className={`rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 ${loan.creditAnalystId ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300' : 'bg-gray-700/60 border border-gray-600 text-gray-500'}`}
                                      >
                                        <option value="">— Unassigned —</option>
                                        {Object.entries(creditAnalystSplits).map(([id, split]) => (
                                          <option key={id} value={id}>
                                            {split.displayName}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </td>
                                )}
                                {/* Same basis as the subtotal above it and the reconciliation
                                    table: NET of assistant/analyst allocations when any exist
                                    (equal to the share when none do), so rows sum to subtotals. */}
                                <td className={`px-4 py-3 text-right font-semibold ${loan.isManualAdjustment ? (loan.brokerNetCents < 0 ? 'text-rose-400' : 'text-green-400') : loan.isArrears || loan.isDischarged || loan.isReferrerDeduction ? 'text-gray-500' : loan.isUntagged ? 'text-amber-400 italic' : loan.brokerNetCents < 0 ? 'text-rose-400' : 'text-green-400'}`} title={hasAllocations && !loan.isArrears && !loan.isDischarged && !loan.isReferrerDeduction ? `Share ${formatCurrencyWithCents(loan.brokerShareCents)} less ${formatCurrencyWithCents(loan.assistantTotalCents + loan.analystTotalCents)} allocated to assistants/analysts` : undefined}>{notConfigured && !loan.isManualAdjustment ? <span className="text-gray-500 italic font-normal text-xs">Not configured</span> : loan.isManualAdjustment ? formatCurrencyWithCents(loan.brokerNetCents) : loan.isReferrerDeduction ? <span title="Referrer fee paid by the company. Not deducted from your share.">—</span> : loan.isArrears || loan.isDischarged ? '—' : loan.isUntagged ? `~${formatCurrencyWithCents(loan.brokerNetCents)}*` : formatCurrencyWithCents(loan.brokerNetCents)}</td>
                                <td className="px-4 py-3"><div className="flex items-center gap-1.5"><LoanStatusBadge loan={loan} />{loan.isManualAdjustment && loan.manualAdjustmentId && (userData?.role === 'super_admin' || userData?.role === 'administrator') && (<button onClick={() => setVoidConfirm({ isOpen: true, adjustmentId: loan.manualAdjustmentId! })} className="p-1 rounded hover:bg-rose-500/20 text-gray-500 hover:text-rose-400 transition-colors" title="Void this adjustment"><X className="w-3 h-3" /></button>)}</div></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="p-4 border-t border-gray-700 flex items-center justify-between shrink-0">
              <p className="text-xs text-gray-500">* Pre-March 2026 loans without a tag default to Company Introduction ({companyIntroPct}%). New loans require explicit tagging. Discharged/arrears loans highlighted. Gross Commission is before the broker split.</p>
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-400">{loanTags.length} payments · {distinctLoanCount(loanTags)} loans · {yearGroups.reduce((s, y) => s + y.months.length, 0)} months{treeIsFiltered && ` (${yearGroups.reduce((s, y) => s + y.paymentCount, 0)} payments shown)`}</p>
                {tagsHasMore && <span className="text-xs text-amber-400">Loading loan tags…</span>}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ═══ MANUAL ADJUSTMENT MODAL ═══ */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowAdjustmentModal(false); resetAdjForm(); }} />
          <div className="relative bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20"><FileText className="w-5 h-5 text-blue-400" /></div>
                <div>
                  <h2 className="text-lg font-bold text-white">Add Manual Adjustment</h2>
                  <p className="text-sm text-gray-400">{selectedBrokerInfo?.name || 'Broker'}</p>
                </div>
              </div>
              <button onClick={() => { setShowAdjustmentModal(false); resetAdjForm(); }} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5">
              {/* Row 1: Type + Loan ID */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Adjustment Type <span className="text-red-400">*</span></label>
                  <select
                    value={adjForm.adjustmentType}
                    onChange={e => setAdjForm(prev => ({ ...prev, adjustmentType: e.target.value as any }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select type...</option>
                    <option value="upfront_commission">Upfront Commission</option>
                    <option value="trail_commission">Trail Commission</option>
                    <option value="clawback">Clawback / Deduction</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Loan ID / Reference <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={adjForm.loanId}
                    onChange={e => setAdjForm(prev => ({ ...prev, loanId: e.target.value }))}
                    placeholder="e.g., 666562"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 1b: Introduction Type + Percentage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Introduction Type</label>
                  <select
                    value={adjForm.introductionType}
                    onChange={e => {
                      const val = e.target.value as 'broker' | 'company';
                      setAdjForm(prev => ({
                        ...prev,
                        introductionType: val,
                        introductionPct: val === 'broker' ? brokerIntroPct : companyIntroPct,
                      }));
                    }}
                    className={`w-full rounded-lg px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      adjForm.introductionType === 'broker'
                        ? 'bg-blue-500/20 border border-blue-500/40 text-blue-300'
                        : 'bg-purple-500/20 border border-purple-500/40 text-purple-300'
                    }`}
                  >
                    <option value="broker">Broker ({brokerIntroPct}%)</option>
                    <option value="company">Company ({companyIntroPct}%)</option>
                    {threeTierActive && <option value="self_sourced">Self-Sourced ({upfrontSelfSourcedPct}/{trailSelfSourcedPct}%)</option>}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Split Percentage</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={adjForm.introductionPct}
                      onChange={e => setAdjForm(prev => ({ ...prev, introductionPct: parseInt(e.target.value, 10) || 0 }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">%</span>
                  </div>
                </div>
              </div>

              {/* Row 2: Customer + Lender */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Customer Name</label>
                  <input
                    type="text"
                    value={adjForm.customerName}
                    onChange={e => setAdjForm(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="e.g., Jessica De Gouw"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Lender</label>
                  <input
                    type="text"
                    value={adjForm.lender}
                    onChange={e => setAdjForm(prev => ({ ...prev, lender: e.target.value }))}
                    placeholder="e.g., Brighten"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 3: Commission + GST */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Commission ex GST ($) <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                    <input
                      type="text"
                      value={adjForm.commissionDollars}
                      onChange={e => setAdjForm(prev => ({ ...prev, commissionDollars: e.target.value }))}
                      placeholder="e.g., -2340.00"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-3 py-2.5 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Use negative for clawbacks/deductions</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                    GST ($)
                    <label className="inline-flex items-center gap-1.5 ml-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={adjForm.gstAuto}
                        onChange={e => setAdjForm(prev => ({ ...prev, gstAuto: e.target.checked }))}
                        className="w-3.5 h-3.5 rounded border-gray-500 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="text-xs text-gray-500">Auto 10%</span>
                    </label>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                    <input
                      type="text"
                      value={adjForm.gstAuto ? (adjForm.commissionDollars ? (parseFloat(adjForm.commissionDollars.replace(/,/g, '')) * 0.1).toFixed(2) : '') : adjForm.gstDollars}
                      onChange={e => setAdjForm(prev => ({ ...prev, gstDollars: e.target.value }))}
                      disabled={adjForm.gstAuto}
                      placeholder={adjForm.gstAuto ? 'Auto-calculated' : 'e.g., -234.00'}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Period + Settlement Date + Loan Amount */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Period Month <span className="text-red-400">*</span></label>
                  <MonthPicker
                    value={adjForm.periodMonth}
                    onChange={(v) => setAdjForm(prev => ({ ...prev, periodMonth: v }))}
                    placeholder="Select period month"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Settlement Date</label>
                  <input
                    type="date"
                    value={adjForm.settlementDate}
                    onChange={e => setAdjForm(prev => ({ ...prev, settlementDate: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1.5 block">Loan Amount ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                    <input
                      type="text"
                      value={adjForm.loanAmountDollars}
                      onChange={e => setAdjForm(prev => ({ ...prev, loanAmountDollars: e.target.value }))}
                      placeholder="e.g., 500000"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">Reason <span className="text-red-400">*</span> <span className="text-xs text-gray-500">(min 10 characters)</span></label>
                <textarea
                  value={adjForm.reason}
                  onChange={e => setAdjForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Brighten clawback discharged commission for Jessica De Gouw, broker share 65%"
                  rows={3}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className={`text-xs mt-1 ${adjForm.reason.trim().length >= 10 ? 'text-green-400' : 'text-gray-500'}`}>
                  {adjForm.reason.trim().length}/10 characters
                </p>
              </div>

              {/* Preview */}
              {adjForm.adjustmentType && adjForm.commissionDollars && (
                <div className="bg-vlt-dark border border-gray-700 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">Preview</p>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
                    <div className="text-gray-400">Broker</div>
                    <div className="text-white font-medium">{selectedBrokerInfo?.name || '—'}</div>
                    <div className="text-gray-400">Type</div>
                    <div className="text-white font-medium">
                      {{ upfront_commission: 'Upfront Commission', trail_commission: 'Trail Commission', clawback: 'Clawback / Deduction', other: 'Other' }[adjForm.adjustmentType] || adjForm.adjustmentType}
                    </div>
                    {adjForm.customerName && <><div className="text-gray-400">Customer</div><div className="text-white">{adjForm.customerName}</div></>}
                    {adjForm.lender && <><div className="text-gray-400">Lender</div><div className="text-white">{adjForm.lender}</div></>}
                    <div className="text-gray-400">Commission</div>
                    <div className={`font-bold ${parseFloat(adjForm.commissionDollars.replace(/,/g, '')) < 0 ? 'text-rose-400' : 'text-green-400'}`}>
                      {formatCurrencyWithCents(Math.round(parseFloat(adjForm.commissionDollars.replace(/,/g, '')) * 100) || 0)}
                    </div>
                    <div className="text-gray-400">Period</div>
                    <div className="text-white">{formatMonthLabel(adjForm.periodMonth)}</div>
                    {(() => {
                      const commCents = Math.round(parseFloat(adjForm.commissionDollars.replace(/,/g, '')) * 100) || 0;
                      const pct = adjForm.introductionPct || 0;
                      const brokerShareCents = Math.round(commCents * pct / 100);
                      return (
                        <>
                          <div className="text-gray-400">Broker Share ({pct}%)</div>
                          <div className={`font-bold ${brokerShareCents < 0 ? 'text-rose-400' : 'text-green-400'}`}>
                            {formatCurrencyWithCents(brokerShareCents)}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
              <button
                onClick={() => { setShowAdjustmentModal(false); resetAdjForm(); }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustmentSubmit}
                disabled={adjustmentSubmitting || !adjForm.adjustmentType || !adjForm.loanId || !adjForm.commissionDollars || adjForm.reason.trim().length < 10}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adjustmentSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {adjustmentSubmitting ? 'Submitting...' : 'Confirm Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={voidConfirm.isOpen}
        title="Void Manual Adjustment"
        message="Are you sure you want to void this manual adjustment? This will remove it from the commission calculations. This action cannot be undone."
        confirmLabel="Void Adjustment"
        confirmVariant="danger"
        onConfirm={() => handleVoidAdjustment(voidConfirm.adjustmentId)}
        onCancel={() => setVoidConfirm({ isOpen: false, adjustmentId: '' })}
      />
      {/* PR C — Commission Structure activation confirmation. */}
      <ConfirmDialog
        isOpen={showStructureModal}
        title={threeTierActive ? 'Revert to 2-Tier Structure?' : 'Activate 3-Tier Commission Structure?'}
        message={
          threeTierActive
            ? 'Reverting will hide the 3-tier panel and return this broker to the standard Broker / Company introduction split. Existing self-sourced loan tags and tier percentages stay on disk but are ignored by the commission compute path. Trail visibility resets to "All loans". You can re-activate any time.'
            : 'Activating will show the 3-tier panel (Internal Referral / Repeat / Self-Sourced) and trail-visibility controls for THIS broker only. Other brokers in the same company will not see this feature. The commission_config saved next will record the new tier structure.'
        }
        confirmLabel={threeTierActive ? 'Revert to 2-Tier' : 'Activate 3-Tier'}
        confirmVariant={threeTierActive ? 'danger' : 'primary'}
        onConfirm={() => handleToggleStructure()}
        onCancel={() => setShowStructureModal(false)}
      />
    </div>
  );
};

export default BrokerCommissionSettingsPage;
